import { BaseEntity, EntitySaveOptions, EntityDeleteOptions, CompositeKey, ValidationResult, ValidationErrorInfo, ValidationErrorType, Metadata, ProviderType, DatabaseProviderBase, RunView } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { z } from "zod";

     
 
/**
 * zod schema definition for the entity Assignment Rules
 */
export const IzzyAssignmentRuleSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization (bizapps-common Organization) whose tickets this rule routes.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Human-readable rule name (e.g. "VIP Customer Routing", "Billing Routing").`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(1000)
        * * Description: Optional longer description of what the rule does.`),
    Priority: z.number().describe(`
        * * Field Name: Priority
        * * Display Name: Priority
        * * SQL Data Type: int
        * * Default Value: 100
        * * Description: Evaluation order; rules are applied in ascending Priority (lower runs first). Defaults to 100.`),
    Conditions: z.string().describe(`
        * * Field Name: Conditions
        * * Display Name: Conditions
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON match expression: { "operator": "AND"|"OR", "rules": [ { "field", "op", "value" } ] }. Field paths reference the issue and derived context (e.g. issue.type, issue.title, issue.priority, issue.requester.organization.planLevel); ops include equals/contains/>=/<=/>/</in.`),
    Actions: z.string().describe(`
        * * Field Name: Actions
        * * Display Name: Actions
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON actions applied when the rule matches: { "assignTeam": "<TicketTeam ID>", "setPriority": "Low"|"Medium"|"High"|"Critical" }.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether the rule is active and evaluated. Inactive rules are skipped.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyAssignmentRuleEntityType = z.infer<typeof IzzyAssignmentRuleSchema>;

/**
 * zod schema definition for the entity Automation Rules
 */
export const IzzyAutomationRuleSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization (bizapps-common Organization) whose tickets this rule automates.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Rule Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Human-readable rule name (e.g. "Escalate Overdue Critical").`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(1000)
        * * Description: Optional longer description of what the rule does.`),
    TriggerEvent: z.union([z.literal('csat.received'), z.literal('issue.assigned'), z.literal('issue.created'), z.literal('issue.escalated'), z.literal('issue.replied'), z.literal('issue.status_changed'), z.literal('issue.updated'), z.literal('sla.breached'), z.literal('sla.warning')]).describe(`
        * * Field Name: TriggerEvent
        * * Display Name: Trigger Event
        * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * csat.received
    *   * issue.assigned
    *   * issue.created
    *   * issue.escalated
    *   * issue.replied
    *   * issue.status_changed
    *   * issue.updated
    *   * sla.breached
    *   * sla.warning
        * * Description: The ticket lifecycle event that fires this rule: issue.created, issue.updated, issue.status_changed, issue.assigned, issue.replied, issue.escalated, sla.warning, sla.breached, or csat.received.`),
    Sequence: z.number().describe(`
        * * Field Name: Sequence
        * * Display Name: Sequence
        * * SQL Data Type: int
        * * Default Value: 100
        * * Description: Evaluation order among rules sharing the same TriggerEvent; lower runs first. Defaults to 100.`),
    Conditions: z.string().describe(`
        * * Field Name: Conditions
        * * Display Name: Conditions
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON match expression: { "operator": "AND"|"OR", "rules": [ { "field", "op", "value" } ] }. Same shape as AssignmentRule; ops include equals/not_equals/contains/not_contains/in/not_in/>/>=/</<=/is_empty/is_not_empty.`),
    Actions: z.string().describe(`
        * * Field Name: Actions
        * * Display Name: Actions
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON action list run when the rule matches: { "actions": [ { "type": "set_field"|"transition_status"|"assign_team"|"assign_agent"|"add_comment", ... } ] }.`),
    IsActive: z.boolean().describe(`
        * * Field Name: IsActive
        * * Display Name: Is Active
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether the rule is active and evaluated. Inactive rules are skipped.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization Name
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyAutomationRuleEntityType = z.infer<typeof IzzyAutomationRuleSchema>;

/**
 * zod schema definition for the entity Channel Actions
 */
export const IzzyChannelActionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ChannelID: z.string().describe(`
        * * Field Name: ChannelID
        * * Display Name: Channel ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: Channels (vwChannels.ID)`),
    ActionID: z.string().describe(`
        * * Field Name: ActionID
        * * Display Name: Action ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)`),
    Status: z.union([z.literal('Active'), z.literal('Disabled'), z.literal('Pending'), z.literal('Revoked')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Disabled
    *   * Pending
    *   * Revoked
        * * Description: Action status at channel level: Active, Pending, Revoked, Disabled`),
    Sequence: z.number().describe(`
        * * Field Name: Sequence
        * * Display Name: Sequence
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Execution order when multiple actions are available`),
    Configuration: z.string().nullable().describe(`
        * * Field Name: Configuration
        * * Display Name: Configuration
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON configuration for this action at channel level`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CredentialID: z.string().nullable().describe(`
        * * Field Name: CredentialID
        * * Display Name: Credential ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
        * * Description: Reference to the credential used for this action at channel level`),
    Channel: z.string().describe(`
        * * Field Name: Channel
        * * Display Name: Channel
        * * SQL Data Type: nvarchar(255)`),
    Action: z.string().describe(`
        * * Field Name: Action
        * * Display Name: Action
        * * SQL Data Type: nvarchar(425)`),
    Credential: z.string().nullable().describe(`
        * * Field Name: Credential
        * * Display Name: Credential
        * * SQL Data Type: nvarchar(200)`),
});

export type IzzyChannelActionEntityType = z.infer<typeof IzzyChannelActionSchema>;

/**
 * zod schema definition for the entity Channel Extensions
 */
export const IzzyChannelExtensionSchema = z.object({
    ChannelID: z.string().describe(`
        * * Field Name: ChannelID
        * * Display Name: Channel ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: Channels (vwChannels.ID)`),
    ActionInheritMode: z.union([z.literal('Channel'), z.literal('ChannelType'), z.literal('Combined'), z.literal('Organization')]).describe(`
        * * Field Name: ActionInheritMode
        * * Display Name: Action Inherit Mode
        * * SQL Data Type: nvarchar(20)
        * * Default Value: ChannelType
    * * Value List Type: List
    * * Possible Values 
    *   * Channel
    *   * ChannelType
    *   * Combined
    *   * Organization`),
    AIConfigurationID: z.string().nullable().describe(`
        * * Field Name: AIConfigurationID
        * * Display Name: AI Configuration ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: AI Configurations (vwAIConfigurations.ID)`),
    CredentialID: z.string().nullable().describe(`
        * * Field Name: CredentialID
        * * Display Name: Credential ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Channel: z.string().describe(`
        * * Field Name: Channel
        * * Display Name: Channel
        * * SQL Data Type: nvarchar(255)`),
    AIConfiguration: z.string().nullable().describe(`
        * * Field Name: AIConfiguration
        * * Display Name: AI Configuration
        * * SQL Data Type: nvarchar(100)`),
    Credential: z.string().nullable().describe(`
        * * Field Name: Credential
        * * Display Name: Credential
        * * SQL Data Type: nvarchar(200)`),
});

export type IzzyChannelExtensionEntityType = z.infer<typeof IzzyChannelExtensionSchema>;

/**
 * zod schema definition for the entity Channel Message Attachments
 */
export const IzzyChannelMessageAttachmentSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ChannelMessageID: z.string().describe(`
        * * Field Name: ChannelMessageID
        * * Display Name: Channel Message
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)`),
    ArtifactID: z.string().describe(`
        * * Field Name: ArtifactID
        * * Display Name: Artifact
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Artifacts (vwArtifacts.ID)`),
    ExternalSystemAttachmentID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemAttachmentID
        * * Display Name: External Attachment ID
        * * SQL Data Type: nvarchar(500)
        * * Description: The external attachment ID from the source system (e.g., Microsoft Graph attachment ID). Used for idempotent ingestion - lets the channel processor detect already-persisted attachments without re-downloading their content.`),
    DisplayOrder: z.number().describe(`
        * * Field Name: DisplayOrder
        * * Display Name: Display Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Display ordering for attachments within a single message. Preserves the order returned by the source system (e.g., Microsoft Graph) so UI rendering is deterministic across reloads.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Direction: z.union([z.literal('Inbound'), z.literal('Outbound')]).describe(`
        * * Field Name: Direction
        * * Display Name: Direction
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Inbound
    * * Value List Type: List
    * * Possible Values 
    *   * Inbound
    *   * Outbound
        * * Description: Which way the attachment travelled: Inbound = received on the original message and persisted at ingestion; Outbound = added by staff to Izzy's reply in Review & Approve and sent with the approved response. Both directions share the same ChannelMessage row, since the reply is sent against the inbound message's record.`),
    Source: z.union([z.literal('Attachment'), z.literal('EmbeddedDataUri'), z.literal('InlineCid')]).describe(`
        * * Field Name: Source
        * * Display Name: Source
        * * SQL Data Type: nvarchar(30)
        * * Default Value: Attachment
    * * Value List Type: List
    * * Possible Values 
    *   * Attachment
    *   * EmbeddedDataUri
    *   * InlineCid
        * * Description: Where the attached content came from. Attachment = a real file attachment on the inbound message (the original behaviour). InlineCid = an image pasted inline in the mail client and referenced from the HTML body by a cid: URL; its bytes are also rewritten into MessageContent for display. EmbeddedDataUri = an image the sender embedded directly in the HTML body as a data: URI. Inline and embedded rows exist so the vision pipeline can describe screenshots the same way it reads file attachments.`),
    IsDecorative: z.boolean().describe(`
        * * Field Name: IsDecorative
        * * Display Name: Is Decorative
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: True when the image carries no information for a support reply: signature logos, award badges, tracking pixels, spacers. Set either by the ingestion size gate (tiny images are never sent to the vision model) or by the vision model's classification. Decorative rows do not count toward the agent's hasAttachments gate and are hidden in the attachment drawer by default.`),
    ChannelMessage: z.string().nullable().describe(`
        * * Field Name: ChannelMessage
        * * Display Name: Channel Message Name
        * * SQL Data Type: nvarchar(500)`),
    Artifact: z.string().describe(`
        * * Field Name: Artifact
        * * Display Name: Artifact Name
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyChannelMessageAttachmentEntityType = z.infer<typeof IzzyChannelMessageAttachmentSchema>;

/**
 * zod schema definition for the entity Channel Message Notes
 */
export const IzzyChannelMessageNoteSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ChannelMessageID: z.string().describe(`
        * * Field Name: ChannelMessageID
        * * Display Name: Channel Message
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    Body: z.string().describe(`
        * * Field Name: Body
        * * Display Name: Note Body
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Plain-text note body written by the staff member.`),
    AuthorPersonID: z.string().nullable().describe(`
        * * Field Name: AuthorPersonID
        * * Display Name: Author
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    AuthorEmail: z.string().nullable().describe(`
        * * Field Name: AuthorEmail
        * * Display Name: Author Email
        * * SQL Data Type: nvarchar(255)
        * * Description: Email of the staff member who wrote the note, captured at write time. Fallback attribution when no bizapps Person is linked to the user.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    ChannelMessage: z.string().nullable().describe(`
        * * Field Name: ChannelMessage
        * * Display Name: Channel Message Name
        * * SQL Data Type: nvarchar(500)`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization Name
        * * SQL Data Type: nvarchar(255)`),
    AuthorPerson: z.string().nullable().describe(`
        * * Field Name: AuthorPerson
        * * Display Name: Author Name
        * * SQL Data Type: nvarchar(250)`),
});

export type IzzyChannelMessageNoteEntityType = z.infer<typeof IzzyChannelMessageNoteSchema>;

/**
 * zod schema definition for the entity Channel Messages
 */
export const IzzyChannelMessageSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ChannelID: z.string().describe(`
        * * Field Name: ChannelID
        * * Display Name: Channel
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: Channels (vwChannels.ID)`),
    ChannelRunID: z.string().nullable().describe(`
        * * Field Name: ChannelRunID
        * * Display Name: Channel Run
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: Channel Runs (vwChannelRuns.ID)`),
    ExternalSystemRecordID: z.string().nullable().describe(`
        * * Field Name: ExternalSystemRecordID
        * * Display Name: External System ID
        * * SQL Data Type: nvarchar(MAX)
        * * Description: External system message ID for deduplication`),
    ReceivedAt: z.date().describe(`
        * * Field Name: ReceivedAt
        * * Display Name: Received At
        * * SQL Data Type: datetimeoffset
        * * Description: When the message was received`),
    Subject: z.string().nullable().describe(`
        * * Field Name: Subject
        * * Display Name: Subject
        * * SQL Data Type: nvarchar(500)
        * * Description: Message subject line (for email)`),
    MessageContent: z.string().describe(`
        * * Field Name: MessageContent
        * * Display Name: Message Content
        * * SQL Data Type: nvarchar(MAX)
        * * Description: The message body content`),
    IsSecure: z.boolean().describe(`
        * * Field Name: IsSecure
        * * Display Name: Is Secure
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether this message contains sensitive/secure content`),
    ParentID: z.string().nullable().describe(`
        * * Field Name: ParentID
        * * Display Name: Parent
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)`),
    ThreadID: z.string().nullable().describe(`
        * * Field Name: ThreadID
        * * Display Name: Thread ID
        * * SQL Data Type: nvarchar(255)
        * * Description: Thread identifier for grouping related messages`),
    PersonID: z.string().nullable().describe(`
        * * Field Name: PersonID
        * * Display Name: Person
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    Sender: z.string().describe(`
        * * Field Name: Sender
        * * Display Name: Sender
        * * SQL Data Type: nvarchar(500)
        * * Description: Sender email/phone/identifier`),
    Recipient: z.string().describe(`
        * * Field Name: Recipient
        * * Display Name: Recipient
        * * SQL Data Type: nvarchar(500)
        * * Description: Recipient email/phone/identifier`),
    GenerationStatus: z.union([z.literal('Creating Reply'), z.literal('Failed'), z.literal('Generated'), z.literal('Manual'), z.literal('Read'), z.literal('Skipped')]).describe(`
        * * Field Name: GenerationStatus
        * * Display Name: Generation Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Read
    * * Value List Type: List
    * * Possible Values 
    *   * Creating Reply
    *   * Failed
    *   * Generated
    *   * Manual
    *   * Read
    *   * Skipped
        * * Description: AI generation status: Read, Creating Reply, Generated, Skipped, Failed`),
    AIAgentRunID: z.string().nullable().describe(`
        * * Field Name: AIAgentRunID
        * * Display Name: AI Agent Run
        * * SQL Data Type: uniqueidentifier`),
    InboundVectorJSON: z.string().nullable().describe(`
        * * Field Name: InboundVectorJSON
        * * Display Name: Inbound Vector
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Vector embedding of inbound message for similarity search`),
    ReplyVectorJSON: z.string().nullable().describe(`
        * * Field Name: ReplyVectorJSON
        * * Display Name: Reply Vector
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Vector embedding of reply for similarity search`),
    VectorModelID: z.string().nullable().describe(`
        * * Field Name: VectorModelID
        * * Display Name: Vector Model
        * * SQL Data Type: uniqueidentifier`),
    ApprovalStatus: z.union([z.literal('Approved'), z.literal('Escalated'), z.literal('Not Required'), z.literal('Pending'), z.literal('Rejected')]).nullable().describe(`
        * * Field Name: ApprovalStatus
        * * Display Name: Approval Status
        * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Approved
    *   * Escalated
    *   * Not Required
    *   * Pending
    *   * Rejected
        * * Description: Approval status: Pending, Approved, Rejected, Not Required`),
    ApprovalFeedback: z.string().nullable().describe(`
        * * Field Name: ApprovalFeedback
        * * Display Name: Approval Feedback
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Feedback from approver on why approved/rejected`),
    GeneratedReplyContent: z.string().nullable().describe(`
        * * Field Name: GeneratedReplyContent
        * * Display Name: Generated Reply
        * * SQL Data Type: nvarchar(MAX)
        * * Description: AI-generated reply content`),
    ApprovedReplyContent: z.string().nullable().describe(`
        * * Field Name: ApprovedReplyContent
        * * Display Name: Approved Reply
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Final approved reply content (may be edited from generated)`),
    ApprovedAt: z.date().nullable().describe(`
        * * Field Name: ApprovedAt
        * * Display Name: Approved At
        * * SQL Data Type: datetimeoffset
        * * Description: When the reply was approved`),
    ApprovedByPersonID: z.string().nullable().describe(`
        * * Field Name: ApprovedByPersonID
        * * Display Name: Approved By
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    GeneratedAt: z.date().nullable().describe(`
        * * Field Name: GeneratedAt
        * * Display Name: Generated At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp when Izzy finished generating the response for this message`),
    SentAt: z.date().nullable().describe(`
        * * Field Name: SentAt
        * * Display Name: Sent At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp when the response was actually sent to the recipient`),
    AIConfidenceScore: z.number().nullable().describe(`
        * * Field Name: AIConfidenceScore
        * * Display Name: AI Confidence Score
        * * SQL Data Type: decimal(5, 2)
        * * Description: AI-generated confidence score for the response quality (0-100 percentage)`),
    AIConfidenceReason: z.string().nullable().describe(`
        * * Field Name: AIConfidenceReason
        * * Display Name: AI Confidence Reason
        * * SQL Data Type: nvarchar(MAX)
        * * Description: AI-generated explanation for the confidence score, including any concerns or areas of uncertainty`),
    AIAutoApproved: z.boolean().nullable().describe(`
        * * Field Name: AIAutoApproved
        * * Display Name: AI Auto Approved
        * * SQL Data Type: bit
        * * Description: Whether this message was automatically approved based on confidence score meeting or exceeding the threshold`),
    AIEvaluatedAt: z.date().nullable().describe(`
        * * Field Name: AIEvaluatedAt
        * * Display Name: AI Evaluated At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp when the AI confidence evaluation was performed`),
    MessageFormat: z.string().nullable().describe(`
        * * Field Name: MessageFormat
        * * Display Name: Message Format
        * * SQL Data Type: nvarchar(20)
        * * Description: Format of the inbound message content. Values: HTML, PlainText, Markdown. Used to determine reply format.`),
    SkipCategory: z.string().nullable().describe(`
        * * Field Name: SkipCategory
        * * Display Name: Skip Category
        * * SQL Data Type: nvarchar(100)
        * * Description: Category indicating why the message was skipped. From early rejection: spam, automated-message, prank, phishing. From decision rejection: legal-advice, financial-dispute, explicit-human-request, urgent-sensitive-personal, urgent-sensitive-business, policy-exception, complex-technical, password-reset-failure, marketing-speaker, inappropriate, out-of-scope`),
    SkipReasoning: z.string().nullable().describe(`
        * * Field Name: SkipReasoning
        * * Display Name: Skip Reasoning
        * * SQL Data Type: nvarchar(MAX)
        * * Description: AI-generated reasoning explaining why the message was skipped or rejected`),
    IsSpam: z.boolean().describe(`
        * * Field Name: IsSpam
        * * Display Name: Is Spam
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Flag indicating the message was identified as spam. Used to filter spam out of pending message lists.`),
    ErrorType: z.union([z.literal('AgentFailure'), z.literal('ChannelConfigFailure'), z.literal('CredentialFailure'), z.literal('Other'), z.literal('ProviderNotFound'), z.literal('RateLimited'), z.literal('SendFailure'), z.literal('Timeout'), z.literal('ValidationFailure')]).nullable().describe(`
        * * Field Name: ErrorType
        * * Display Name: Error Type
        * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * AgentFailure
    *   * ChannelConfigFailure
    *   * CredentialFailure
    *   * Other
    *   * ProviderNotFound
    *   * RateLimited
    *   * SendFailure
    *   * Timeout
    *   * ValidationFailure
        * * Description: Type of error that occurred during processing or sending. Values: AgentFailure, SendFailure, CredentialFailure, ProviderNotFound, ValidationFailure, ChannelConfigFailure, Timeout, RateLimited, Other`),
    ErrorMessage: z.string().nullable().describe(`
        * * Field Name: ErrorMessage
        * * Display Name: Error Message
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Detailed error message describing what went wrong during processing or sending. Useful for debugging and support.`),
    SentContent: z.string().nullable().describe(`
        * * Field Name: SentContent
        * * Display Name: Sent Content
        * * SQL Data Type: nvarchar(MAX)
        * * Description: The exact content that was sent to the recipient, including any signatures and branding. Populated after successful send for audit trail.`),
    ForcedGeneration: z.boolean().describe(`
        * * Field Name: ForcedGeneration
        * * Display Name: Forced Generation
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Flag indicating the message was force-generated from Escalated status. Prevents auto-approval for safety.`),
    EscalatedAt: z.date().nullable().describe(`
        * * Field Name: EscalatedAt
        * * Display Name: Escalated At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp when the message was escalated (either automatically by filtering or manually by user)`),
    EscalatedByPersonID: z.string().nullable().describe(`
        * * Field Name: EscalatedByPersonID
        * * Display Name: Escalated By
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Contact who manually escalated the message. NULL for automatic escalations by filtering agent.`),
    AssignedToPersonID: z.string().nullable().describe(`
        * * Field Name: AssignedToPersonID
        * * Display Name: Assigned To
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Contact assigned to review/handle this message`),
    AssignedAt: z.date().nullable().describe(`
        * * Field Name: AssignedAt
        * * Display Name: Assigned At
        * * SQL Data Type: datetimeoffset
        * * Description: When the message was assigned to the current contact`),
    QualityFeedback: z.union([z.literal('Down'), z.literal('Up')]).nullable().describe(`
        * * Field Name: QualityFeedback
        * * Display Name: Quality Feedback
        * * SQL Data Type: nvarchar(4)
    * * Value List Type: List
    * * Possible Values 
    *   * Down
    *   * Up
        * * Description: Staff quality rating for the AI-generated response. Up = response was helpful and accurate. Down = response had issues. Used for RLHF feedback loops.`),
    QualityFeedbackAt: z.date().nullable().describe(`
        * * Field Name: QualityFeedbackAt
        * * Display Name: Quality Feedback At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp when the quality feedback was submitted by staff.`),
    QualityFeedbackByPersonID: z.string().nullable().describe(`
        * * Field Name: QualityFeedbackByPersonID
        * * Display Name: Quality Feedback By
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    MessageCategory: z.string().nullable().describe(`
        * * Field Name: MessageCategory
        * * Display Name: Message Category
        * * SQL Data Type: nvarchar(100)
        * * Description: AI-assigned category for the inbound message, selected from the organization's predefined MessageCategories setting. Used for mandatory reporting on message topic distribution.`),
    MessageContentPreview: z.string().nullable().describe(`
        * * Field Name: MessageContentPreview
        * * Display Name: Content Preview
        * * SQL Data Type: nvarchar(500)
        * * Description: HTML-stripped, truncated preview of MessageContent for fast list-view rendering. Populated at write time and backfilled for existing rows.`),
    IssueID: z.string().nullable().describe(`
        * * Field Name: IssueID
        * * Display Name: Issue
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
        * * Description: Optional FK to the bizapps-issues Issue (ticket) this message belongs to. NULL if ticket management is not enabled or the message is unlinked. Multiple messages across different channels and threads link to the same Issue — this is the cross-channel ticket link.`),
    ToRecipients: z.string().nullable().describe(`
        * * Field Name: ToRecipients
        * * Display Name: To Recipients
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Inbound only: all To recipients of the message as received, comma-delimited bare email addresses (includes the channel's own address; suppression happens at send time, not capture time). NULL when the provider does not supply recipient lists and on outbound rows.`),
    CCRecipients: z.string().nullable().describe(`
        * * Field Name: CCRecipients
        * * Display Name: CC Recipients
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Inbound only: all CC recipients of the message as received, comma-delimited bare email addresses. NULL when there was no CC, when the provider does not supply recipient lists, and on outbound rows.`),
    SentToRecipients: z.string().nullable().describe(`
        * * Field Name: SentToRecipients
        * * Display Name: Sent To Recipients
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Outbound only: the To recipients Izzy actually sent this message to, comma-delimited, recorded at send time for audit. NULL on inbound rows.`),
    SentCCRecipients: z.string().nullable().describe(`
        * * Field Name: SentCCRecipients
        * * Display Name: Sent CC Recipients
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Outbound only: the CC recipients Izzy actually sent this message to after suppression and caps, comma-delimited, recorded at send time for audit. NULL on inbound rows and when nothing was CC'd.`),
    OutboundType: z.union([z.literal('Forward'), z.literal('New'), z.literal('Reply')]).nullable().describe(`
        * * Field Name: OutboundType
        * * Display Name: Outbound Type
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Forward
    *   * New
    *   * Reply
        * * Description: Outbound only: Reply, Forward, or New (staff-composed fresh outbound with no inbound origin). Distinguishes outbound kinds on the ticket timeline. Stamped at creation time for staff-originated rows (a non-null value with SentAt null is a staff draft awaiting approval) and at send time for AI replies. NULL on inbound rows and on outbound rows created before this feature (rendered as replies).`),
    SentByPersonID: z.string().nullable().describe(`
        * * Field Name: SentByPersonID
        * * Display Name: Sent By
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)`),
    MessageContentText: z.string().nullable().describe(`
        * * Field Name: MessageContentText
        * * Display Name: Message Content (Plain Text)
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Plain-text rendering of MessageContent: HTML stripped, entities decoded, whitespace collapsed, capped at 32,000 characters. Written whenever MessageContent changes and indexed for full-text search; never shown to users. NULL on rows saved before the backfill.`),
    Channel: z.string().describe(`
        * * Field Name: Channel
        * * Display Name: Channel Name
        * * SQL Data Type: nvarchar(255)`),
    Parent: z.string().nullable().describe(`
        * * Field Name: Parent
        * * Display Name: Parent Message
        * * SQL Data Type: nvarchar(500)`),
    Person: z.string().nullable().describe(`
        * * Field Name: Person
        * * Display Name: Person Name
        * * SQL Data Type: nvarchar(250)`),
    ApprovedByPerson: z.string().nullable().describe(`
        * * Field Name: ApprovedByPerson
        * * Display Name: Approved By Name
        * * SQL Data Type: nvarchar(250)`),
    EscalatedByPerson: z.string().nullable().describe(`
        * * Field Name: EscalatedByPerson
        * * Display Name: Escalated By Name
        * * SQL Data Type: nvarchar(250)`),
    AssignedToPerson: z.string().nullable().describe(`
        * * Field Name: AssignedToPerson
        * * Display Name: Assigned To Name
        * * SQL Data Type: nvarchar(250)`),
    QualityFeedbackByPerson: z.string().nullable().describe(`
        * * Field Name: QualityFeedbackByPerson
        * * Display Name: Quality Feedback By Name
        * * SQL Data Type: nvarchar(250)`),
    Issue: z.string().nullable().describe(`
        * * Field Name: Issue
        * * Display Name: Issue
        * * SQL Data Type: nvarchar(50)`),
    SentByPerson: z.string().nullable().describe(`
        * * Field Name: SentByPerson
        * * Display Name: Sent By Name
        * * SQL Data Type: nvarchar(250)`),
    RootParentID: z.string().nullable().describe(`
        * * Field Name: RootParentID
        * * Display Name: Root Parent
        * * SQL Data Type: uniqueidentifier`),
    ParentIDDepth: z.number().nullable().describe(`
        * * Field Name: ParentIDDepth
        * * Display Name: Parent Depth
        * * SQL Data Type: int`),
    ParentIDPath: z.string().nullable().describe(`
        * * Field Name: ParentIDPath
        * * Display Name: Parent Path
        * * SQL Data Type: nvarchar(MAX)`),
    ParentIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: ParentIDIsLeaf
        * * Display Name: Is Leaf
        * * SQL Data Type: bit`),
    ParentIDChildCount: z.number().nullable().describe(`
        * * Field Name: ParentIDChildCount
        * * Display Name: Child Count
        * * SQL Data Type: int`),
});

export type IzzyChannelMessageEntityType = z.infer<typeof IzzyChannelMessageSchema>;

/**
 * zod schema definition for the entity Channel Type Actions
 */
export const IzzyChannelTypeActionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    ChannelTypeID: z.string().describe(`
        * * Field Name: ChannelTypeID
        * * Display Name: Channel Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: Channel Types (vwChannelTypes.ID)`),
    ActionID: z.string().describe(`
        * * Field Name: ActionID
        * * Display Name: Action ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)`),
    Status: z.union([z.literal('Active'), z.literal('Disabled')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Disabled
        * * Description: Action status at channel type level: Active, Disabled`),
    Sequence: z.number().describe(`
        * * Field Name: Sequence
        * * Display Name: Sequence
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Execution order when multiple actions are available`),
    Configuration: z.string().nullable().describe(`
        * * Field Name: Configuration
        * * Display Name: Configuration
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON configuration for this action at channel type level`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CredentialID: z.string().nullable().describe(`
        * * Field Name: CredentialID
        * * Display Name: Credential ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
        * * Description: Reference to the credential used for this action at channel type level`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    ChannelType: z.string().describe(`
        * * Field Name: ChannelType
        * * Display Name: Channel Type
        * * SQL Data Type: nvarchar(255)`),
    Action: z.string().describe(`
        * * Field Name: Action
        * * Display Name: Action
        * * SQL Data Type: nvarchar(425)`),
    Credential: z.string().nullable().describe(`
        * * Field Name: Credential
        * * Display Name: Credential
        * * SQL Data Type: nvarchar(200)`),
});

export type IzzyChannelTypeActionEntityType = z.infer<typeof IzzyChannelTypeActionSchema>;

/**
 * zod schema definition for the entity Channel Type Extensions
 */
export const IzzyChannelTypeExtensionSchema = z.object({
    ChannelTypeID: z.string().describe(`
        * * Field Name: ChannelTypeID
        * * Display Name: Channel Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: Channel Types (vwChannelTypes.ID)`),
    ActionInheritMode: z.union([z.literal('ChannelType'), z.literal('Combined'), z.literal('Organization')]).describe(`
        * * Field Name: ActionInheritMode
        * * Display Name: Action Inherit Mode
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Combined
    * * Value List Type: List
    * * Possible Values 
    *   * ChannelType
    *   * Combined
    *   * Organization`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    ChannelType: z.string().describe(`
        * * Field Name: ChannelType
        * * Display Name: Channel Type
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyChannelTypeExtensionEntityType = z.infer<typeof IzzyChannelTypeExtensionSchema>;

/**
 * zod schema definition for the entity Credential Extensions
 */
export const IzzyCredentialExtensionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CredentialID: z.string().describe(`
        * * Field Name: CredentialID
        * * Display Name: Credential
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)`),
    CredentialNotes: z.string().nullable().describe(`
        * * Field Name: CredentialNotes
        * * Display Name: Notes
        * * SQL Data Type: nvarchar(500)
        * * Description: Freeform notes about this credential`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Credential: z.string().describe(`
        * * Field Name: Credential
        * * Display Name: Credential Name
        * * SQL Data Type: nvarchar(200)`),
});

export type IzzyCredentialExtensionEntityType = z.infer<typeof IzzyCredentialExtensionSchema>;

/**
 * zod schema definition for the entity Credential Type Extensions
 */
export const IzzyCredentialTypeExtensionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    CredentialTypeID: z.string().describe(`
        * * Field Name: CredentialTypeID
        * * Display Name: Credential Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Credential Types (vwCredentialTypes.ID)`),
    Status: z.string().describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
        * * Description: Active or Inactive status for this credential type`),
    Website: z.string().nullable().describe(`
        * * Field Name: Website
        * * Display Name: Website
        * * SQL Data Type: nvarchar(500)
        * * Description: Provider website URL`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Category: z.string().nullable().describe(`
        * * Field Name: Category
        * * Display Name: Category
        * * SQL Data Type: nvarchar(50)`),
    CredentialType: z.string().describe(`
        * * Field Name: CredentialType
        * * Display Name: Credential Type
        * * SQL Data Type: nvarchar(100)`),
});

export type IzzyCredentialTypeExtensionEntityType = z.infer<typeof IzzyCredentialTypeExtensionSchema>;

/**
 * zod schema definition for the entity Issue Ticket Numbers
 */
export const IzzyIssueTicketNumberSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    IssueID: z.string().describe(`
        * * Field Name: IssueID
        * * Display Name: Issue
        * * SQL Data Type: uniqueidentifier
        * * Description: The bizapps-issues Issue this ticket number belongs to (UNIQUE — one ticket number per Issue).`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    TicketNumber: z.string().describe(`
        * * Field Name: TicketNumber
        * * Display Name: Ticket Number
        * * SQL Data Type: nvarchar(50)
        * * Description: The formatted org-facing ticket number, e.g. 'ACME-0042'. {PREFIX}-{padded seq} as returned by spAssignNextTicketNumber.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyIssueTicketNumberEntityType = z.infer<typeof IzzyIssueTicketNumberSchema>;

/**
 * zod schema definition for the entity Izzy Action Categories
 */
export const IzzyIzzyActionCategorySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(100)
        * * Description: Category display name`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)
        * * Description: Description of actions in this category`),
    ParentID: z.string().nullable().describe(`
        * * Field Name: ParentID
        * * Display Name: Parent ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Izzy Action Categories (vwIzzyActionCategories.ID)`),
    IconClass: z.string().nullable().describe(`
        * * Field Name: IconClass
        * * Display Name: Icon Class
        * * SQL Data Type: nvarchar(100)
        * * Description: Font Awesome icon class for UI display`),
    SortOrder: z.number().describe(`
        * * Field Name: SortOrder
        * * Display Name: Sort Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Display order within parent category`),
    Status: z.union([z.literal('Active'), z.literal('Inactive')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
        * * Description: Category status: Active, Inactive`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Parent: z.string().nullable().describe(`
        * * Field Name: Parent
        * * Display Name: Parent
        * * SQL Data Type: nvarchar(100)`),
    RootParentID: z.string().nullable().describe(`
        * * Field Name: RootParentID
        * * Display Name: Root Parent ID
        * * SQL Data Type: uniqueidentifier`),
    ParentIDDepth: z.number().nullable().describe(`
        * * Field Name: ParentIDDepth
        * * Display Name: Depth
        * * SQL Data Type: int`),
    ParentIDPath: z.string().nullable().describe(`
        * * Field Name: ParentIDPath
        * * Display Name: Path
        * * SQL Data Type: nvarchar(MAX)`),
    ParentIDIsLeaf: z.boolean().nullable().describe(`
        * * Field Name: ParentIDIsLeaf
        * * Display Name: Is Leaf
        * * SQL Data Type: bit`),
    ParentIDChildCount: z.number().nullable().describe(`
        * * Field Name: ParentIDChildCount
        * * Display Name: Child Count
        * * SQL Data Type: int`),
});

export type IzzyIzzyActionCategoryEntityType = z.infer<typeof IzzyIzzyActionCategorySchema>;

/**
 * zod schema definition for the entity Izzy Action Organizations
 */
export const IzzyIzzyActionOrganizationSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    IzzyActionID: z.string().describe(`
        * * Field Name: IzzyActionID
        * * Display Name: Action
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Izzy Actions (vwIzzyActions.ID)`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    Status: z.union([z.literal('Active'), z.literal('Revoked')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Revoked
        * * Description: Grant status: Active, Revoked`),
    GrantedAt: z.date().describe(`
        * * Field Name: GrantedAt
        * * Display Name: Granted At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()
        * * Description: When the action was granted to this organization`),
    ExpiresAt: z.date().nullable().describe(`
        * * Field Name: ExpiresAt
        * * Display Name: Expires At
        * * SQL Data Type: datetimeoffset
        * * Description: When this grant expires (null = never)`),
    Notes: z.string().nullable().describe(`
        * * Field Name: Notes
        * * Display Name: Notes
        * * SQL Data Type: nvarchar(500)
        * * Description: Admin notes about why this was granted`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    IzzyAction: z.string().describe(`
        * * Field Name: IzzyAction
        * * Display Name: Action Name
        * * SQL Data Type: nvarchar(255)`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization Name
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyIzzyActionOrganizationEntityType = z.infer<typeof IzzyIzzyActionOrganizationSchema>;

/**
 * zod schema definition for the entity Izzy Actions
 */
export const IzzyIzzyActionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    ActionID: z.string().describe(`
        * * Field Name: ActionID
        * * Display Name: Action ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Display name for the action`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Detailed description of what the action does`),
    CategoryID: z.string().nullable().describe(`
        * * Field Name: CategoryID
        * * Display Name: Category
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Izzy Action Categories (vwIzzyActionCategories.ID)`),
    IconClass: z.string().nullable().describe(`
        * * Field Name: IconClass
        * * Display Name: Icon Class
        * * SQL Data Type: nvarchar(100)
        * * Description: Font Awesome icon class for UI display`),
    Status: z.union([z.literal('Active'), z.literal('Coming Soon'), z.literal('Deprecated')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Coming Soon
    *   * Deprecated
        * * Description: Action status: Active, Deprecated, Coming Soon`),
    IsPublic: z.boolean().describe(`
        * * Field Name: IsPublic
        * * Display Name: Is Public
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether this action is available to all orgs (vs private)`),
    MinimumPlanLevel: z.number().describe(`
        * * Field Name: MinimumPlanLevel
        * * Display Name: Minimum Plan Level
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Minimum plan level required to use this action`),
    RequiresCredentials: z.boolean().describe(`
        * * Field Name: RequiresCredentials
        * * Display Name: Requires Credentials
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: Whether this action requires credentials to be configured`),
    CredentialSchema: z.string().nullable().describe(`
        * * Field Name: CredentialSchema
        * * Display Name: Credential Schema
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON Schema defining required credential fields`),
    ConfigurationSchema: z.string().nullable().describe(`
        * * Field Name: ConfigurationSchema
        * * Display Name: Configuration Schema
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON Schema defining configuration options`),
    DocumentationURL: z.string().nullable().describe(`
        * * Field Name: DocumentationURL
        * * Display Name: Documentation URL
        * * SQL Data Type: nvarchar(1024)
        * * Description: URL to action documentation`),
    SortOrder: z.number().describe(`
        * * Field Name: SortOrder
        * * Display Name: Sort Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Display order within category`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Action: z.string().describe(`
        * * Field Name: Action
        * * Display Name: Action
        * * SQL Data Type: nvarchar(425)`),
    Category: z.string().nullable().describe(`
        * * Field Name: Category
        * * Display Name: Category
        * * SQL Data Type: nvarchar(100)`),
});

export type IzzyIzzyActionEntityType = z.infer<typeof IzzyIzzyActionSchema>;

/**
 * zod schema definition for the entity Macros
 */
export const IzzyMacroSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization (bizapps-common Organization) the macro belongs to.`),
    OwnerPersonID: z.string().nullable().describe(`
        * * Field Name: OwnerPersonID
        * * Display Name: Owner Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The owning agent (bizapps-common Person) for a Personal-scope macro; NULL for shared (Team/Org) macros.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Short name shown in the /macro picker.`),
    Content: z.string().describe(`
        * * Field Name: Content
        * * Display Name: Content
        * * SQL Data Type: nvarchar(MAX)
        * * Description: The reply body inserted when the macro is selected; may contain {{placeholders}} like {{requester.FirstName}}, {{ticket.IssueNumber}}, {{agent.FirstName}}, {{organization.Name}}.`),
    ContentFormat: z.union([z.literal('HTML'), z.literal('Text')]).describe(`
        * * Field Name: ContentFormat
        * * Display Name: Content Format
        * * SQL Data Type: nvarchar(10)
        * * Default Value: HTML
    * * Value List Type: List
    * * Possible Values 
    *   * HTML
    *   * Text
        * * Description: Format of Content: HTML or Text.`),
    FieldChanges: z.string().nullable().describe(`
        * * Field Name: FieldChanges
        * * Display Name: Field Changes
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Optional JSON field changes applied to the ticket when the macro is used, e.g. { "Status": "Resolved", "Priority": "High" }.`),
    Scope: z.union([z.literal('Org'), z.literal('Personal'), z.literal('Team')]).describe(`
        * * Field Name: Scope
        * * Display Name: Scope
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Org
    * * Value List Type: List
    * * Possible Values 
    *   * Org
    *   * Personal
    *   * Team
        * * Description: Visibility scope: Personal (only OwnerPersonID), Team (only TeamID members), or Org (everyone in the org).`),
    TeamID: z.string().nullable().describe(`
        * * Field Name: TeamID
        * * Display Name: Team ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Ticket Teams (vwTicketTeams.ID)
        * * Description: The team (izzy.TicketTeam) for a Team-scope macro.`),
    UsageCount: z.number().describe(`
        * * Field Name: UsageCount
        * * Display Name: Usage Count
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: How many times the macro has been used (incremented on insertion).`),
    Status: z.union([z.literal('Active'), z.literal('Inactive')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
        * * Description: Whether the macro is active and offered in the picker.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    OwnerPerson: z.string().nullable().describe(`
        * * Field Name: OwnerPerson
        * * Display Name: Owner Person
        * * SQL Data Type: nvarchar(250)`),
    Team: z.string().nullable().describe(`
        * * Field Name: Team
        * * Display Name: Team
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyMacroEntityType = z.infer<typeof IzzyMacroSchema>;

/**
 * zod schema definition for the entity Organization Actions
 */
export const IzzyOrganizationActionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)`),
    ActionID: z.string().describe(`
        * * Field Name: ActionID
        * * Display Name: Action
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)`),
    Status: z.union([z.literal('Active'), z.literal('Disabled')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Disabled
        * * Description: Action status at org level: Active, Disabled`),
    Sequence: z.number().describe(`
        * * Field Name: Sequence
        * * Display Name: Sequence
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Execution order when multiple actions are available`),
    Configuration: z.string().nullable().describe(`
        * * Field Name: Configuration
        * * Display Name: Configuration
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON configuration for this action at org level`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    CredentialID: z.string().nullable().describe(`
        * * Field Name: CredentialID
        * * Display Name: Credential ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
        * * Description: Reference to the credential used for this action at org level`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization Name
        * * SQL Data Type: nvarchar(255)`),
    Action: z.string().describe(`
        * * Field Name: Action
        * * Display Name: Action Name
        * * SQL Data Type: nvarchar(425)`),
    Credential: z.string().nullable().describe(`
        * * Field Name: Credential
        * * Display Name: Credential Name
        * * SQL Data Type: nvarchar(200)`),
});

export type IzzyOrganizationActionEntityType = z.infer<typeof IzzyOrganizationActionSchema>;

/**
 * zod schema definition for the entity Pending Crawl Results
 */
export const IzzyPendingCrawlResultSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization this crawl result belongs to`),
    CrawledURL: z.string().describe(`
        * * Field Name: CrawledURL
        * * Display Name: Crawled URL
        * * SQL Data Type: nvarchar(2048)
        * * Description: The URL that was crawled`),
    CrawledAt: z.date().describe(`
        * * Field Name: CrawledAt
        * * Display Name: Crawled At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()
        * * Description: Timestamp when the crawl was executed`),
    Status: z.union([z.literal('Accepted'), z.literal('Dismissed'), z.literal('Pending')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(50)
        * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Accepted
    *   * Dismissed
    *   * Pending
        * * Description: Review status: Pending = awaiting admin review, Accepted = admin applied changes, Dismissed = admin rejected`),
    ResultJSON: z.string().nullable().describe(`
        * * Field Name: ResultJSON
        * * Display Name: Result JSON
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Raw JSON output from the Setup KB Generator agent containing proposed setting sections`),
    ReviewedAt: z.date().nullable().describe(`
        * * Field Name: ReviewedAt
        * * Display Name: Reviewed At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp when an admin reviewed this result`),
    ReviewedByPersonID: z.string().nullable().describe(`
        * * Field Name: ReviewedByPersonID
        * * Display Name: Reviewed By Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The contact (admin) who reviewed this result`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
    ReviewedByPerson: z.string().nullable().describe(`
        * * Field Name: ReviewedByPerson
        * * Display Name: Reviewed By Person
        * * SQL Data Type: nvarchar(250)`),
});

export type IzzyPendingCrawlResultEntityType = z.infer<typeof IzzyPendingCrawlResultSchema>;

/**
 * zod schema definition for the entity Person Skills
 */
export const IzzyPersonSkillSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The agent (bizapps-common Person) who holds the skill.`),
    SkillID: z.string().describe(`
        * * Field Name: SkillID
        * * Display Name: Skill ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Skills (vwSkills.ID)
        * * Description: The skill held (izzy.Skill).`),
    ProficiencyLevel: z.number().describe(`
        * * Field Name: ProficiencyLevel
        * * Display Name: Proficiency Level
        * * SQL Data Type: int
        * * Default Value: 3
        * * Description: Proficiency from 1 (novice) to 5 (expert); used to score agents for skills-based routing.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Person: z.string().describe(`
        * * Field Name: Person
        * * Display Name: Person
        * * SQL Data Type: nvarchar(250)`),
    Skill: z.string().describe(`
        * * Field Name: Skill
        * * Display Name: Skill
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyPersonSkillEntityType = z.infer<typeof IzzyPersonSkillSchema>;

/**
 * zod schema definition for the entity SLA Policies
 */
export const IzzySLAPolicySchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization that owns this SLA policy.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Display name of the policy (e.g. "Critical-priority tickets").`),
    Priority: z.union([z.literal('All'), z.literal('Critical'), z.literal('High'), z.literal('Low'), z.literal('Medium')]).describe(`
        * * Field Name: Priority
        * * Display Name: Priority
        * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * All
    *   * Critical
    *   * High
    *   * Low
    *   * Medium
        * * Description: Ticket priority this policy applies to: Low/Medium/High/Critical, or All to match any priority. The most specific matching policy (by SortOrder) wins.`),
    FirstResponseTargetMinutes: z.number().nullable().describe(`
        * * Field Name: FirstResponseTargetMinutes
        * * Display Name: First Response Target (Minutes)
        * * SQL Data Type: int
        * * Description: Target time, in minutes, from ticket creation to first agent response. NULL means no first-response target.`),
    ResolutionTargetMinutes: z.number().nullable().describe(`
        * * Field Name: ResolutionTargetMinutes
        * * Display Name: Resolution Target (Minutes)
        * * SQL Data Type: int
        * * Description: Target time, in minutes, from ticket creation to resolution. NULL means no resolution target.`),
    BusinessHoursOnly: z.boolean().describe(`
        * * Field Name: BusinessHoursOnly
        * * Display Name: Business Hours Only
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: When 1, SLA timers count only business hours (per the org business-hours configuration); when 0, they count calendar time.`),
    EscalationPersonID: z.string().nullable().describe(`
        * * Field Name: EscalationPersonID
        * * Display Name: Escalation Person
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: Optional person to escalate to when the SLA is breached or nears breach.`),
    EscalationTeamID: z.string().nullable().describe(`
        * * Field Name: EscalationTeamID
        * * Display Name: Escalation Team
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Ticket Teams (vwTicketTeams.ID)
        * * Description: Optional team to escalate to when the SLA is breached or nears breach.`),
    SortOrder: z.number().describe(`
        * * Field Name: SortOrder
        * * Display Name: Sort Order
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Evaluation order when multiple policies could match; lower numbers are checked first. The first matching active policy is applied.`),
    Status: z.union([z.literal('Active'), z.literal('Inactive')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
        * * Description: Lifecycle status: Active = used for matching, Inactive = retained but not applied.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization Name
        * * SQL Data Type: nvarchar(255)`),
    EscalationPerson: z.string().nullable().describe(`
        * * Field Name: EscalationPerson
        * * Display Name: Escalation Person Name
        * * SQL Data Type: nvarchar(250)`),
    EscalationTeam: z.string().nullable().describe(`
        * * Field Name: EscalationTeam
        * * Display Name: Escalation Team Name
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzySLAPolicyEntityType = z.infer<typeof IzzySLAPolicySchema>;

/**
 * zod schema definition for the entity Scopes
 */
export const IzzyScopeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(100)
        * * Description: Scope identifier (e.g., messages:read)`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(500)
        * * Description: Human-readable description of what this scope allows`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
});

export type IzzyScopeEntityType = z.infer<typeof IzzyScopeSchema>;

/**
 * zod schema definition for the entity Setting Extensions
 */
export const IzzySettingExtensionSchema = z.object({
    SettingID: z.string().describe(`
        * * Field Name: SettingID
        * * Display Name: Setting ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: BC: Settings (vwSettings.ID)`),
    Icon: z.string().nullable().describe(`
        * * Field Name: Icon
        * * Display Name: Icon
        * * SQL Data Type: nvarchar(100)
        * * Description: Icon identifier for the setting`),
    IsCategoryController: z.boolean().describe(`
        * * Field Name: IsCategoryController
        * * Display Name: Is Category Controller
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: When true, this setting controls which category is shown to the user`),
    ValueType: z.string().nullable().describe(`
        * * Field Name: ValueType
        * * Display Name: Value Type
        * * SQL Data Type: nvarchar(50)
        * * Description: Expected value type for the setting (e.g. boolean, string, number)`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    AllowedValues: z.string().nullable().describe(`
        * * Field Name: AllowedValues
        * * Display Name: Allowed Values
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Optional JSON array of allowed values for a List-type setting, e.g. ["None","RoundRobin","LeastLoad"]. When ValueType is 'List', the Izzy Settings UI renders a dropdown populated from this array; NULL for non-List settings.`),
    Setting: z.string().describe(`
        * * Field Name: Setting
        * * Display Name: Setting
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzySettingExtensionEntityType = z.infer<typeof IzzySettingExtensionSchema>;

/**
 * zod schema definition for the entity Skill Issue Types
 */
export const IzzySkillIssueTypeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SkillID: z.string().describe(`
        * * Field Name: SkillID
        * * Display Name: Skill ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Skills (vwSkills.ID)
        * * Description: The required skill (izzy.Skill).`),
    IssueTypeID: z.string().describe(`
        * * Field Name: IssueTypeID
        * * Display Name: Issue Type ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issue Types (vwIssueTypes.ID)
        * * Description: The ticket type (bizapps-issues IssueType) that requires the skill.`),
    MinProficiency: z.number().nullable().describe(`
        * * Field Name: MinProficiency
        * * Display Name: Min Proficiency
        * * SQL Data Type: int
        * * Description: Optional minimum proficiency (1-5) an agent must have in the skill to be considered for this type; NULL means any proficiency qualifies.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Skill: z.string().describe(`
        * * Field Name: Skill
        * * Display Name: Skill
        * * SQL Data Type: nvarchar(255)`),
    IssueType: z.string().describe(`
        * * Field Name: IssueType
        * * Display Name: Issue Type
        * * SQL Data Type: nvarchar(100)`),
});

export type IzzySkillIssueTypeEntityType = z.infer<typeof IzzySkillIssueTypeSchema>;

/**
 * zod schema definition for the entity Skills
 */
export const IzzySkillSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization (bizapps-common Organization) that defines this skill.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Skill name, e.g. "Billing", "API Support", "Spanish".`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(1000)
        * * Description: Optional description of the skill.`),
    Status: z.union([z.literal('Active'), z.literal('Inactive')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
        * * Description: Whether the skill is active. Inactive skills are ignored by routing.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzySkillEntityType = z.infer<typeof IzzySkillSchema>;

/**
 * zod schema definition for the entity Ticket Merges
 */
export const IzzyTicketMergeSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    SurvivorIssueID: z.string().nullable().describe(`
        * * Field Name: SurvivorIssueID
        * * Display Name: Survivor Ticket
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
        * * Description: The ticket (bizapps-issues Issue) that remains and absorbs the loser's messages/comments. NULL for a standalone person merge.`),
    LoserIssueID: z.string().nullable().describe(`
        * * Field Name: LoserIssueID
        * * Display Name: Loser Ticket
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
        * * Description: The ticket (bizapps-issues Issue) merged into the survivor and tombstoned. NULL for a standalone person merge.`),
    MergedByPersonID: z.string().nullable().describe(`
        * * Field Name: MergedByPersonID
        * * Display Name: Merged By
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The staff Person who performed the merge; NULL for system/automated merges.`),
    MergedAt: z.date().describe(`
        * * Field Name: MergedAt
        * * Display Name: Merged At
        * * SQL Data Type: datetimeoffset
        * * Default Value: sysdatetimeoffset()
        * * Description: When the merge was performed.`),
    PersonMerged: z.boolean().describe(`
        * * Field Name: PersonMerged
        * * Display Name: Reporter Merge Performed
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: 1 when reporter People were merged into one (consolidating contact methods); 0 for a ticket-only merge.`),
    SurvivorPersonID: z.string().nullable().describe(`
        * * Field Name: SurvivorPersonID
        * * Display Name: Survivor Reporter
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The surviving reporter Person when PersonMerged=1; NULL otherwise.`),
    LoserPersonID: z.string().nullable().describe(`
        * * Field Name: LoserPersonID
        * * Display Name: Loser Reporter
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The merged-away reporter Person (tombstoned Inactive) when PersonMerged=1; NULL otherwise.`),
    LoserPriorStatusID: z.string().nullable().describe(`
        * * Field Name: LoserPriorStatusID
        * * Display Name: Loser Prior Status
        * * SQL Data Type: uniqueidentifier
        * * Description: The loser Issue's StatusID immediately before the merge, captured so un-merge can restore it.`),
    MovedManifest: z.string().nullable().describe(`
        * * Field Name: MovedManifest
        * * Display Name: Moved Manifest
        * * SQL Data Type: nvarchar(MAX)
        * * Description: JSON manifest of exactly what the merge moved (re-pointed FK references, and any 1:1 satellite rows deleted, with prior values/snapshots). Replayed in reverse by un-merge.`),
    Status: z.union([z.literal('Merged'), z.literal('Reversed')]).describe(`
        * * Field Name: Status
        * * Display Name: Merge Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Merged
    * * Value List Type: List
    * * Possible Values 
    *   * Merged
    *   * Reversed
        * * Description: Merge lifecycle: Merged = in effect; Reversed = un-merged (manifest replayed back).`),
    ReversedAt: z.date().nullable().describe(`
        * * Field Name: ReversedAt
        * * Display Name: Reversed At
        * * SQL Data Type: datetimeoffset
        * * Description: When the merge was reversed; NULL while still in effect.`),
    ReversedByPersonID: z.string().nullable().describe(`
        * * Field Name: ReversedByPersonID
        * * Display Name: Reversed By
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The staff Person who reversed the merge; NULL while still in effect.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    SurvivorIssue: z.string().nullable().describe(`
        * * Field Name: SurvivorIssue
        * * Display Name: Survivor Issue
        * * SQL Data Type: nvarchar(50)`),
    LoserIssue: z.string().nullable().describe(`
        * * Field Name: LoserIssue
        * * Display Name: Loser Issue
        * * SQL Data Type: nvarchar(50)`),
    MergedByPerson: z.string().nullable().describe(`
        * * Field Name: MergedByPerson
        * * Display Name: Merged By Name
        * * SQL Data Type: nvarchar(250)`),
    SurvivorPerson: z.string().nullable().describe(`
        * * Field Name: SurvivorPerson
        * * Display Name: Survivor Reporter Name
        * * SQL Data Type: nvarchar(250)`),
    LoserPerson: z.string().nullable().describe(`
        * * Field Name: LoserPerson
        * * Display Name: Loser Reporter Name
        * * SQL Data Type: nvarchar(250)`),
    ReversedByPerson: z.string().nullable().describe(`
        * * Field Name: ReversedByPerson
        * * Display Name: Reversed By Name
        * * SQL Data Type: nvarchar(250)`),
});

export type IzzyTicketMergeEntityType = z.infer<typeof IzzyTicketMergeSchema>;

/**
 * zod schema definition for the entity Ticket Number Sequences
 */
export const IzzyTicketNumberSequenceSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization this counter belongs to (UNIQUE — one sequence row per org). FK to bizapps-common Organization.`),
    TicketPrefix: z.string().describe(`
        * * Field Name: TicketPrefix
        * * Display Name: Ticket Prefix
        * * SQL Data Type: nvarchar(20)
        * * Default Value: TKT
        * * Description: Per-org ticket prefix (e.g. 'ACME'). Admin-editable; defaults to 'TKT'. Combined with the sequence as {PREFIX}-{padded seq}. Changing it does not renumber existing tickets — the counter keeps climbing.`),
    NextSequenceNumber: z.number().describe(`
        * * Field Name: NextSequenceNumber
        * * Display Name: Next Sequence Number
        * * SQL Data Type: int
        * * Default Value: 1
        * * Description: The next sequence value to assign for this org. Incremented atomically (UPDLOCK/HOLDLOCK) by spAssignNextTicketNumber.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization Name
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyTicketNumberSequenceEntityType = z.infer<typeof IzzyTicketNumberSequenceSchema>;

/**
 * zod schema definition for the entity Ticket SLA States
 */
export const IzzyTicketSLAStateSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    IssueID: z.string().describe(`
        * * Field Name: IssueID
        * * Display Name: Ticket
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
        * * Description: The ticket (bizapps-issues Issue) this SLA clock tracks. One SLA state per Issue.`),
    SLAPolicyID: z.string().nullable().describe(`
        * * Field Name: SLAPolicyID
        * * Display Name: SLA Policy ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: SLA Policies (vwSLAPolicies.ID)
        * * Description: The SLA policy matched to this ticket at creation; NULL if no policy matched.`),
    FirstResponseAt: z.date().nullable().describe(`
        * * Field Name: FirstResponseAt
        * * Display Name: First Response At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp of the first agent/AI response to the ticket; NULL until first response. Stamped once.`),
    FirstResponseDueAt: z.date().nullable().describe(`
        * * Field Name: FirstResponseDueAt
        * * Display Name: First Response Due At
        * * SQL Data Type: datetimeoffset
        * * Description: Deadline for the first response, computed from the policy target (adjusted for paused time and business hours).`),
    ResolutionDueAt: z.date().nullable().describe(`
        * * Field Name: ResolutionDueAt
        * * Display Name: Resolution Due At
        * * SQL Data Type: datetimeoffset
        * * Description: Deadline for resolution, computed from the policy target (adjusted for paused time and business hours).`),
    SLAPausedAt: z.date().nullable().describe(`
        * * Field Name: SLAPausedAt
        * * Display Name: SLA Paused At
        * * SQL Data Type: datetimeoffset
        * * Description: When the ticket last entered a non-counting (paused) status; NULL when the clock is running. Used to accumulate SLAPausedMinutes on resume.`),
    SLAPausedMinutes: z.number().describe(`
        * * Field Name: SLAPausedMinutes
        * * Display Name: SLA Paused Minutes
        * * SQL Data Type: int
        * * Default Value: 0
        * * Description: Total minutes the clock has been paused across all non-counting periods; added to due-date computations.`),
    WarningFiredAt: z.date().nullable().describe(`
        * * Field Name: WarningFiredAt
        * * Display Name: Warning Fired At
        * * SQL Data Type: datetimeoffset
        * * Description: When the pre-breach warning fired for this ticket; NULL until fired. Prevents duplicate warnings.`),
    Breached: z.boolean().describe(`
        * * Field Name: Breached
        * * Display Name: Breached
        * * SQL Data Type: bit
        * * Default Value: 0
        * * Description: 1 when the resolution SLA has been breached.`),
    BreachedAt: z.date().nullable().describe(`
        * * Field Name: BreachedAt
        * * Display Name: Breached At
        * * SQL Data Type: datetimeoffset
        * * Description: Timestamp the breach was recorded; NULL when not breached.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Issue: z.string().nullable().describe(`
        * * Field Name: Issue
        * * Display Name: Issue
        * * SQL Data Type: nvarchar(50)`),
    SLAPolicy: z.string().nullable().describe(`
        * * Field Name: SLAPolicy
        * * Display Name: SLA Policy
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyTicketSLAStateEntityType = z.infer<typeof IzzyTicketSLAStateSchema>;

/**
 * zod schema definition for the entity Ticket Suggestions
 */
export const IzzyTicketSuggestionSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    NewIssueID: z.string().describe(`
        * * Field Name: NewIssueID
        * * Display Name: New Ticket
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
        * * Description: The ticket (bizapps-issues Issue) the follow-up message actually created; this is the ticket whose detail view shows the suggestion banner.`),
    SuggestedIssueID: z.string().describe(`
        * * Field Name: SuggestedIssueID
        * * Display Name: Suggested Ticket
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
        * * Description: The existing open ticket (bizapps-issues Issue) the new ticket may be a follow-up to — the merge target if staff accept the suggestion.`),
    TriggerMessageID: z.string().nullable().describe(`
        * * Field Name: TriggerMessageID
        * * Display Name: Trigger Message ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)
        * * Description: The inbound ChannelMessage that triggered the suggestion (the follow-up); NULL if not retained.`),
    ReporterPersonID: z.string().nullable().describe(`
        * * Field Name: ReporterPersonID
        * * Display Name: Reporter ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The reporter Person both tickets share; provenance for the same-person guard that gated the suggestion.`),
    Reason: z.string().nullable().describe(`
        * * Field Name: Reason
        * * Display Name: Reason
        * * SQL Data Type: nvarchar(1000)
        * * Description: The classifier's short rationale for suggesting the link (shown to staff).`),
    Confidence: z.number().nullable().describe(`
        * * Field Name: Confidence
        * * Display Name: Confidence
        * * SQL Data Type: decimal(5, 4)
        * * Description: Classifier confidence in the suggested link, 0 to 1, when the classifier provides one; NULL otherwise.`),
    Status: z.union([z.literal('Accepted'), z.literal('Dismissed'), z.literal('Open')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Open
    * * Value List Type: List
    * * Possible Values 
    *   * Accepted
    *   * Dismissed
    *   * Open
        * * Description: Suggestion lifecycle: Open = awaiting staff action; Accepted = staff merged the tickets; Dismissed = staff rejected the link.`),
    ResolvedByPersonID: z.string().nullable().describe(`
        * * Field Name: ResolvedByPersonID
        * * Display Name: Resolved By ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The staff Person who accepted or dismissed the suggestion; NULL while Open.`),
    ResolvedAt: z.date().nullable().describe(`
        * * Field Name: ResolvedAt
        * * Display Name: Resolved At
        * * SQL Data Type: datetimeoffset
        * * Description: When the suggestion was accepted or dismissed; NULL while Open.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    NewIssue: z.string().nullable().describe(`
        * * Field Name: NewIssue
        * * Display Name: New Issue
        * * SQL Data Type: nvarchar(50)`),
    SuggestedIssue: z.string().nullable().describe(`
        * * Field Name: SuggestedIssue
        * * Display Name: Suggested Issue
        * * SQL Data Type: nvarchar(50)`),
    TriggerMessage: z.string().nullable().describe(`
        * * Field Name: TriggerMessage
        * * Display Name: Trigger Message
        * * SQL Data Type: nvarchar(500)`),
    ReporterPerson: z.string().nullable().describe(`
        * * Field Name: ReporterPerson
        * * Display Name: Reporter
        * * SQL Data Type: nvarchar(250)`),
    ResolvedByPerson: z.string().nullable().describe(`
        * * Field Name: ResolvedByPerson
        * * Display Name: Resolved By
        * * SQL Data Type: nvarchar(250)`),
});

export type IzzyTicketSuggestionEntityType = z.infer<typeof IzzyTicketSuggestionSchema>;

/**
 * zod schema definition for the entity Ticket Team Members
 */
export const IzzyTicketTeamMemberSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    TeamID: z.string().describe(`
        * * Field Name: TeamID
        * * Display Name: Team
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: Ticket Teams (vwTicketTeams.ID)
        * * Description: The team this membership belongs to.`),
    PersonID: z.string().describe(`
        * * Field Name: PersonID
        * * Display Name: Person
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
        * * Description: The person (support agent) who is a member of the team.`),
    Role: z.union([z.literal('Lead'), z.literal('Member')]).describe(`
        * * Field Name: Role
        * * Display Name: Role
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Member
    * * Value List Type: List
    * * Possible Values 
    *   * Lead
    *   * Member
        * * Description: Member role within the team: Lead or Member.`),
    IsAvailable: z.boolean().describe(`
        * * Field Name: IsAvailable
        * * Display Name: Is Available
        * * SQL Data Type: bit
        * * Default Value: 1
        * * Description: Whether the member is currently available to receive routed tickets (e.g. toggled off when out of office).`),
    MaxConcurrentTickets: z.number().nullable().describe(`
        * * Field Name: MaxConcurrentTickets
        * * Display Name: Max Concurrent Tickets
        * * SQL Data Type: int
        * * Description: Maximum number of concurrently-open tickets the member should hold; NULL means no cap. Used by least-load routing to skip over-capacity agents.`),
    Status: z.union([z.literal('Active'), z.literal('Inactive')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
        * * Description: Lifecycle status: Active = counted for routing, Inactive = retained but not routed to.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Team: z.string().describe(`
        * * Field Name: Team
        * * Display Name: Team Name
        * * SQL Data Type: nvarchar(255)`),
    Person: z.string().describe(`
        * * Field Name: Person
        * * Display Name: Person Name
        * * SQL Data Type: nvarchar(250)`),
});

export type IzzyTicketTeamMemberEntityType = z.infer<typeof IzzyTicketTeamMemberSchema>;

/**
 * zod schema definition for the entity Ticket Teams
 */
export const IzzyTicketTeamSchema = z.object({
    ID: z.string().describe(`
        * * Field Name: ID
        * * Display Name: ID
        * * SQL Data Type: uniqueidentifier
        * * Default Value: newsequentialid()`),
    OrganizationID: z.string().describe(`
        * * Field Name: OrganizationID
        * * Display Name: Organization ID
        * * SQL Data Type: uniqueidentifier
        * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
        * * Description: The organization that owns this team.`),
    Name: z.string().describe(`
        * * Field Name: Name
        * * Display Name: Name
        * * SQL Data Type: nvarchar(255)
        * * Description: Display name of the team (e.g. "Billing", "Tier 1").`),
    Description: z.string().nullable().describe(`
        * * Field Name: Description
        * * Display Name: Description
        * * SQL Data Type: nvarchar(MAX)
        * * Description: Optional longer description of the team's scope or responsibilities.`),
    Status: z.union([z.literal('Active'), z.literal('Inactive')]).describe(`
        * * Field Name: Status
        * * Display Name: Status
        * * SQL Data Type: nvarchar(20)
        * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
        * * Description: Lifecycle status: Active = available for routing, Inactive = retained but not routed to.`),
    __mj_CreatedAt: z.date().describe(`
        * * Field Name: __mj_CreatedAt
        * * Display Name: Created At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    __mj_UpdatedAt: z.date().describe(`
        * * Field Name: __mj_UpdatedAt
        * * Display Name: Updated At
        * * SQL Data Type: datetimeoffset
        * * Default Value: getutcdate()`),
    Organization: z.string().describe(`
        * * Field Name: Organization
        * * Display Name: Organization
        * * SQL Data Type: nvarchar(255)`),
});

export type IzzyTicketTeamEntityType = z.infer<typeof IzzyTicketTeamSchema>;
 
 

/**
 * Assignment Rules - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: AssignmentRule
 * * Base View: vwAssignmentRules
 * * @description An organization-scoped ticket routing rule (PRD v4.0 §9.3). Evaluated in Priority order when a ticket is created: when its Conditions match, its Actions are applied (assign a team, set priority), and the assigned team feeds the org's TicketAutoAssignMode member pick. The rules engine runs in front of the RoundRobin/LeastLoad assignment modes.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Assignment Rules')
export class IzzyAssignmentRuleEntity extends BaseEntity<IzzyAssignmentRuleEntityType> {
    /**
    * Loads the Assignment Rules record from the database
    * @param ID: string - primary key value to load the Assignment Rules record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyAssignmentRuleEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization (bizapps-common Organization) whose tickets this rule routes.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Human-readable rule name (e.g. "VIP Customer Routing", "Billing Routing").
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(1000)
    * * Description: Optional longer description of what the rule does.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Priority
    * * Display Name: Priority
    * * SQL Data Type: int
    * * Default Value: 100
    * * Description: Evaluation order; rules are applied in ascending Priority (lower runs first). Defaults to 100.
    */
    get Priority(): number {
        return this.Get('Priority');
    }
    set Priority(value: number) {
        this.Set('Priority', value);
    }

    /**
    * * Field Name: Conditions
    * * Display Name: Conditions
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON match expression: { "operator": "AND"|"OR", "rules": [ { "field", "op", "value" } ] }. Field paths reference the issue and derived context (e.g. issue.type, issue.title, issue.priority, issue.requester.organization.planLevel); ops include equals/contains/>=/<=/>/</in.
    */
    get Conditions(): string {
        return this.Get('Conditions');
    }
    set Conditions(value: string) {
        this.Set('Conditions', value);
    }

    /**
    * * Field Name: Actions
    * * Display Name: Actions
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON actions applied when the rule matches: { "assignTeam": "<TicketTeam ID>", "setPriority": "Low"|"Medium"|"High"|"Critical" }.
    */
    get Actions(): string {
        return this.Get('Actions');
    }
    set Actions(value: string) {
        this.Set('Actions', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether the rule is active and evaluated. Inactive rules are skipped.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }
}


/**
 * Automation Rules - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: AutomationRule
 * * Base View: vwAutomationRules
 * * @description An organization-scoped event→condition→action automation rule (PRD v4.0 §12). On a ticket lifecycle event, active rules for that event are evaluated in Sequence order; a rule whose Conditions match runs its Actions (set field, transition status, assign team/agent, add comment, etc.). Complements bizapps-issues IssueType hooks and bizapps-tasks overdue notifications.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Automation Rules')
export class IzzyAutomationRuleEntity extends BaseEntity<IzzyAutomationRuleEntityType> {
    /**
    * Loads the Automation Rules record from the database
    * @param ID: string - primary key value to load the Automation Rules record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyAutomationRuleEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization (bizapps-common Organization) whose tickets this rule automates.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Rule Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Human-readable rule name (e.g. "Escalate Overdue Critical").
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(1000)
    * * Description: Optional longer description of what the rule does.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: TriggerEvent
    * * Display Name: Trigger Event
    * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * csat.received
    *   * issue.assigned
    *   * issue.created
    *   * issue.escalated
    *   * issue.replied
    *   * issue.status_changed
    *   * issue.updated
    *   * sla.breached
    *   * sla.warning
    * * Description: The ticket lifecycle event that fires this rule: issue.created, issue.updated, issue.status_changed, issue.assigned, issue.replied, issue.escalated, sla.warning, sla.breached, or csat.received.
    */
    get TriggerEvent(): 'csat.received' | 'issue.assigned' | 'issue.created' | 'issue.escalated' | 'issue.replied' | 'issue.status_changed' | 'issue.updated' | 'sla.breached' | 'sla.warning' {
        return this.Get('TriggerEvent');
    }
    set TriggerEvent(value: 'csat.received' | 'issue.assigned' | 'issue.created' | 'issue.escalated' | 'issue.replied' | 'issue.status_changed' | 'issue.updated' | 'sla.breached' | 'sla.warning') {
        this.Set('TriggerEvent', value);
    }

    /**
    * * Field Name: Sequence
    * * Display Name: Sequence
    * * SQL Data Type: int
    * * Default Value: 100
    * * Description: Evaluation order among rules sharing the same TriggerEvent; lower runs first. Defaults to 100.
    */
    get Sequence(): number {
        return this.Get('Sequence');
    }
    set Sequence(value: number) {
        this.Set('Sequence', value);
    }

    /**
    * * Field Name: Conditions
    * * Display Name: Conditions
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON match expression: { "operator": "AND"|"OR", "rules": [ { "field", "op", "value" } ] }. Same shape as AssignmentRule; ops include equals/not_equals/contains/not_contains/in/not_in/>/>=/</<=/is_empty/is_not_empty.
    */
    get Conditions(): string {
        return this.Get('Conditions');
    }
    set Conditions(value: string) {
        this.Set('Conditions', value);
    }

    /**
    * * Field Name: Actions
    * * Display Name: Actions
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON action list run when the rule matches: { "actions": [ { "type": "set_field"|"transition_status"|"assign_team"|"assign_agent"|"add_comment", ... } ] }.
    */
    get Actions(): string {
        return this.Get('Actions');
    }
    set Actions(value: string) {
        this.Set('Actions', value);
    }

    /**
    * * Field Name: IsActive
    * * Display Name: Is Active
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether the rule is active and evaluated. Inactive rules are skipped.
    */
    get IsActive(): boolean {
        return this.Get('IsActive');
    }
    set IsActive(value: boolean) {
        this.Set('IsActive', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization Name
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }
}


/**
 * Channel Actions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: ChannelAction
 * * Base View: vwChannelActions
 * * @description Channel-specific action configuration (bottom of inheritance)
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Channel Actions')
export class IzzyChannelActionEntity extends BaseEntity<IzzyChannelActionEntityType> {
    /**
    * Loads the Channel Actions record from the database
    * @param ID: string - primary key value to load the Channel Actions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyChannelActionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ChannelID
    * * Display Name: Channel ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: Channels (vwChannels.ID)
    */
    get ChannelID(): string {
        return this.Get('ChannelID');
    }
    set ChannelID(value: string) {
        this.Set('ChannelID', value);
    }

    /**
    * * Field Name: ActionID
    * * Display Name: Action ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)
    */
    get ActionID(): string {
        return this.Get('ActionID');
    }
    set ActionID(value: string) {
        this.Set('ActionID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Disabled
    *   * Pending
    *   * Revoked
    * * Description: Action status at channel level: Active, Pending, Revoked, Disabled
    */
    get Status(): 'Active' | 'Disabled' | 'Pending' | 'Revoked' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Disabled' | 'Pending' | 'Revoked') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: Sequence
    * * Display Name: Sequence
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Execution order when multiple actions are available
    */
    get Sequence(): number {
        return this.Get('Sequence');
    }
    set Sequence(value: number) {
        this.Set('Sequence', value);
    }

    /**
    * * Field Name: Configuration
    * * Display Name: Configuration
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON configuration for this action at channel level
    */
    get Configuration(): string | null {
        return this.Get('Configuration');
    }
    set Configuration(value: string | null) {
        this.Set('Configuration', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CredentialID
    * * Display Name: Credential ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
    * * Description: Reference to the credential used for this action at channel level
    */
    get CredentialID(): string | null {
        return this.Get('CredentialID');
    }
    set CredentialID(value: string | null) {
        this.Set('CredentialID', value);
    }

    /**
    * * Field Name: Channel
    * * Display Name: Channel
    * * SQL Data Type: nvarchar(255)
    */
    get Channel(): string {
        return this.Get('Channel');
    }

    /**
    * * Field Name: Action
    * * Display Name: Action
    * * SQL Data Type: nvarchar(425)
    */
    get Action(): string {
        return this.Get('Action');
    }

    /**
    * * Field Name: Credential
    * * Display Name: Credential
    * * SQL Data Type: nvarchar(200)
    */
    get Credential(): string | null {
        return this.Get('Credential');
    }
}


/**
 * Channel Extensions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: ChannelExtension
 * * Base View: vwChannelExtensions
 * * @description Izzy-specific extension columns for __BCSaaS.Channel. 1:1 by ChannelID.
 * * Primary Key: ChannelID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Channel Extensions')
export class IzzyChannelExtensionEntity extends BaseEntity<IzzyChannelExtensionEntityType> {
    /**
    * Loads the Channel Extensions record from the database
    * @param ChannelID: string - primary key value to load the Channel Extensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyChannelExtensionEntity
    * @method
    * @override
    */
    public async Load(ChannelID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ChannelID', Value: ChannelID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ChannelID
    * * Display Name: Channel ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: Channels (vwChannels.ID)
    */
    get ChannelID(): string {
        return this.Get('ChannelID');
    }
    set ChannelID(value: string) {
        this.Set('ChannelID', value);
    }

    /**
    * * Field Name: ActionInheritMode
    * * Display Name: Action Inherit Mode
    * * SQL Data Type: nvarchar(20)
    * * Default Value: ChannelType
    * * Value List Type: List
    * * Possible Values 
    *   * Channel
    *   * ChannelType
    *   * Combined
    *   * Organization
    */
    get ActionInheritMode(): 'Channel' | 'ChannelType' | 'Combined' | 'Organization' {
        return this.Get('ActionInheritMode');
    }
    set ActionInheritMode(value: 'Channel' | 'ChannelType' | 'Combined' | 'Organization') {
        this.Set('ActionInheritMode', value);
    }

    /**
    * * Field Name: AIConfigurationID
    * * Display Name: AI Configuration ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: AI Configurations (vwAIConfigurations.ID)
    */
    get AIConfigurationID(): string | null {
        return this.Get('AIConfigurationID');
    }
    set AIConfigurationID(value: string | null) {
        this.Set('AIConfigurationID', value);
    }

    /**
    * * Field Name: CredentialID
    * * Display Name: Credential ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
    */
    get CredentialID(): string | null {
        return this.Get('CredentialID');
    }
    set CredentialID(value: string | null) {
        this.Set('CredentialID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Channel
    * * Display Name: Channel
    * * SQL Data Type: nvarchar(255)
    */
    get Channel(): string {
        return this.Get('Channel');
    }

    /**
    * * Field Name: AIConfiguration
    * * Display Name: AI Configuration
    * * SQL Data Type: nvarchar(100)
    */
    get AIConfiguration(): string | null {
        return this.Get('AIConfiguration');
    }

    /**
    * * Field Name: Credential
    * * Display Name: Credential
    * * SQL Data Type: nvarchar(200)
    */
    get Credential(): string | null {
        return this.Get('Credential');
    }
}


/**
 * Channel Message Attachments - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: ChannelMessageAttachment
 * * Base View: vwChannelMessageAttachments
 * * @description Links an inbound or outbound ChannelMessage to the MJ Artifacts that carry its attached content. Inbound rows are created at ingestion for real file attachments (Source = Attachment) and, since the inbound image vision work, for inline cid: images and sender-embedded data: images (Source = InlineCid / EmbeddedDataUri); image rows carry the vision model's description as MJ Artifact Version Attributes. Outbound rows (Direction = Outbound) are staff-attached files on replies. Artifact bytes live in the paired ArtifactVersion (inbound content as a data: URL, outbound as raw base64 for the provider).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Channel Message Attachments')
export class IzzyChannelMessageAttachmentEntity extends BaseEntity<IzzyChannelMessageAttachmentEntityType> {
    /**
    * Loads the Channel Message Attachments record from the database
    * @param ID: string - primary key value to load the Channel Message Attachments record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyChannelMessageAttachmentEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ChannelMessageID
    * * Display Name: Channel Message
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)
    */
    get ChannelMessageID(): string {
        return this.Get('ChannelMessageID');
    }
    set ChannelMessageID(value: string) {
        this.Set('ChannelMessageID', value);
    }

    /**
    * * Field Name: ArtifactID
    * * Display Name: Artifact
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Artifacts (vwArtifacts.ID)
    */
    get ArtifactID(): string {
        return this.Get('ArtifactID');
    }
    set ArtifactID(value: string) {
        this.Set('ArtifactID', value);
    }

    /**
    * * Field Name: ExternalSystemAttachmentID
    * * Display Name: External Attachment ID
    * * SQL Data Type: nvarchar(500)
    * * Description: The external attachment ID from the source system (e.g., Microsoft Graph attachment ID). Used for idempotent ingestion - lets the channel processor detect already-persisted attachments without re-downloading their content.
    */
    get ExternalSystemAttachmentID(): string | null {
        return this.Get('ExternalSystemAttachmentID');
    }
    set ExternalSystemAttachmentID(value: string | null) {
        this.Set('ExternalSystemAttachmentID', value);
    }

    /**
    * * Field Name: DisplayOrder
    * * Display Name: Display Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Display ordering for attachments within a single message. Preserves the order returned by the source system (e.g., Microsoft Graph) so UI rendering is deterministic across reloads.
    */
    get DisplayOrder(): number {
        return this.Get('DisplayOrder');
    }
    set DisplayOrder(value: number) {
        this.Set('DisplayOrder', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Direction
    * * Display Name: Direction
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Inbound
    * * Value List Type: List
    * * Possible Values 
    *   * Inbound
    *   * Outbound
    * * Description: Which way the attachment travelled: Inbound = received on the original message and persisted at ingestion; Outbound = added by staff to Izzy's reply in Review & Approve and sent with the approved response. Both directions share the same ChannelMessage row, since the reply is sent against the inbound message's record.
    */
    get Direction(): 'Inbound' | 'Outbound' {
        return this.Get('Direction');
    }
    set Direction(value: 'Inbound' | 'Outbound') {
        this.Set('Direction', value);
    }

    /**
    * * Field Name: Source
    * * Display Name: Source
    * * SQL Data Type: nvarchar(30)
    * * Default Value: Attachment
    * * Value List Type: List
    * * Possible Values 
    *   * Attachment
    *   * EmbeddedDataUri
    *   * InlineCid
    * * Description: Where the attached content came from. Attachment = a real file attachment on the inbound message (the original behaviour). InlineCid = an image pasted inline in the mail client and referenced from the HTML body by a cid: URL; its bytes are also rewritten into MessageContent for display. EmbeddedDataUri = an image the sender embedded directly in the HTML body as a data: URI. Inline and embedded rows exist so the vision pipeline can describe screenshots the same way it reads file attachments.
    */
    get Source(): 'Attachment' | 'EmbeddedDataUri' | 'InlineCid' {
        return this.Get('Source');
    }
    set Source(value: 'Attachment' | 'EmbeddedDataUri' | 'InlineCid') {
        this.Set('Source', value);
    }

    /**
    * * Field Name: IsDecorative
    * * Display Name: Is Decorative
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: True when the image carries no information for a support reply: signature logos, award badges, tracking pixels, spacers. Set either by the ingestion size gate (tiny images are never sent to the vision model) or by the vision model's classification. Decorative rows do not count toward the agent's hasAttachments gate and are hidden in the attachment drawer by default.
    */
    get IsDecorative(): boolean {
        return this.Get('IsDecorative');
    }
    set IsDecorative(value: boolean) {
        this.Set('IsDecorative', value);
    }

    /**
    * * Field Name: ChannelMessage
    * * Display Name: Channel Message Name
    * * SQL Data Type: nvarchar(500)
    */
    get ChannelMessage(): string | null {
        return this.Get('ChannelMessage');
    }

    /**
    * * Field Name: Artifact
    * * Display Name: Artifact Name
    * * SQL Data Type: nvarchar(255)
    */
    get Artifact(): string {
        return this.Get('Artifact');
    }
}


/**
 * Channel Message Notes - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: ChannelMessageNote
 * * Base View: vwChannelMessageNotes
 * * @description Staff-only internal note attached to a single channel message. Append-only audit trail; never shown to or sent to the customer. Message-level counterpart of the internal comments on tickets. Message notes also surface in the linked ticket's timeline; ticket notes do not flow back to messages.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Channel Message Notes')
export class IzzyChannelMessageNoteEntity extends BaseEntity<IzzyChannelMessageNoteEntityType> {
    /**
    * Loads the Channel Message Notes record from the database
    * @param ID: string - primary key value to load the Channel Message Notes record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyChannelMessageNoteEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ChannelMessageID
    * * Display Name: Channel Message
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)
    */
    get ChannelMessageID(): string {
        return this.Get('ChannelMessageID');
    }
    set ChannelMessageID(value: string) {
        this.Set('ChannelMessageID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: Body
    * * Display Name: Note Body
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Plain-text note body written by the staff member.
    */
    get Body(): string {
        return this.Get('Body');
    }
    set Body(value: string) {
        this.Set('Body', value);
    }

    /**
    * * Field Name: AuthorPersonID
    * * Display Name: Author
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get AuthorPersonID(): string | null {
        return this.Get('AuthorPersonID');
    }
    set AuthorPersonID(value: string | null) {
        this.Set('AuthorPersonID', value);
    }

    /**
    * * Field Name: AuthorEmail
    * * Display Name: Author Email
    * * SQL Data Type: nvarchar(255)
    * * Description: Email of the staff member who wrote the note, captured at write time. Fallback attribution when no bizapps Person is linked to the user.
    */
    get AuthorEmail(): string | null {
        return this.Get('AuthorEmail');
    }
    set AuthorEmail(value: string | null) {
        this.Set('AuthorEmail', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: ChannelMessage
    * * Display Name: Channel Message Name
    * * SQL Data Type: nvarchar(500)
    */
    get ChannelMessage(): string | null {
        return this.Get('ChannelMessage');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization Name
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: AuthorPerson
    * * Display Name: Author Name
    * * SQL Data Type: nvarchar(250)
    */
    get AuthorPerson(): string | null {
        return this.Get('AuthorPerson');
    }
}


/**
 * Channel Messages - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: ChannelMessage
 * * Base View: vwChannelMessages
 * * @description Individual messages received through channels
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Channel Messages')
export class IzzyChannelMessageEntity extends BaseEntity<IzzyChannelMessageEntityType> {
    /**
    * Loads the Channel Messages record from the database
    * @param ID: string - primary key value to load the Channel Messages record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyChannelMessageEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ChannelID
    * * Display Name: Channel
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: Channels (vwChannels.ID)
    */
    get ChannelID(): string {
        return this.Get('ChannelID');
    }
    set ChannelID(value: string) {
        this.Set('ChannelID', value);
    }

    /**
    * * Field Name: ChannelRunID
    * * Display Name: Channel Run
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: Channel Runs (vwChannelRuns.ID)
    */
    get ChannelRunID(): string | null {
        return this.Get('ChannelRunID');
    }
    set ChannelRunID(value: string | null) {
        this.Set('ChannelRunID', value);
    }

    /**
    * * Field Name: ExternalSystemRecordID
    * * Display Name: External System ID
    * * SQL Data Type: nvarchar(MAX)
    * * Description: External system message ID for deduplication
    */
    get ExternalSystemRecordID(): string | null {
        return this.Get('ExternalSystemRecordID');
    }
    set ExternalSystemRecordID(value: string | null) {
        this.Set('ExternalSystemRecordID', value);
    }

    /**
    * * Field Name: ReceivedAt
    * * Display Name: Received At
    * * SQL Data Type: datetimeoffset
    * * Description: When the message was received
    */
    get ReceivedAt(): Date {
        return this.Get('ReceivedAt');
    }
    set ReceivedAt(value: Date) {
        this.Set('ReceivedAt', value);
    }

    /**
    * * Field Name: Subject
    * * Display Name: Subject
    * * SQL Data Type: nvarchar(500)
    * * Description: Message subject line (for email)
    */
    get Subject(): string | null {
        return this.Get('Subject');
    }
    set Subject(value: string | null) {
        this.Set('Subject', value);
    }

    /**
    * * Field Name: MessageContent
    * * Display Name: Message Content
    * * SQL Data Type: nvarchar(MAX)
    * * Description: The message body content
    */
    get MessageContent(): string {
        return this.Get('MessageContent');
    }
    set MessageContent(value: string) {
        this.Set('MessageContent', value);
    }

    /**
    * * Field Name: IsSecure
    * * Display Name: Is Secure
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether this message contains sensitive/secure content
    */
    get IsSecure(): boolean {
        return this.Get('IsSecure');
    }
    set IsSecure(value: boolean) {
        this.Set('IsSecure', value);
    }

    /**
    * * Field Name: ParentID
    * * Display Name: Parent
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)
    */
    get ParentID(): string | null {
        return this.Get('ParentID');
    }
    set ParentID(value: string | null) {
        this.Set('ParentID', value);
    }

    /**
    * * Field Name: ThreadID
    * * Display Name: Thread ID
    * * SQL Data Type: nvarchar(255)
    * * Description: Thread identifier for grouping related messages
    */
    get ThreadID(): string | null {
        return this.Get('ThreadID');
    }
    set ThreadID(value: string | null) {
        this.Set('ThreadID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get PersonID(): string | null {
        return this.Get('PersonID');
    }
    set PersonID(value: string | null) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Sender
    * * Display Name: Sender
    * * SQL Data Type: nvarchar(500)
    * * Description: Sender email/phone/identifier
    */
    get Sender(): string {
        return this.Get('Sender');
    }
    set Sender(value: string) {
        this.Set('Sender', value);
    }

    /**
    * * Field Name: Recipient
    * * Display Name: Recipient
    * * SQL Data Type: nvarchar(500)
    * * Description: Recipient email/phone/identifier
    */
    get Recipient(): string {
        return this.Get('Recipient');
    }
    set Recipient(value: string) {
        this.Set('Recipient', value);
    }

    /**
    * * Field Name: GenerationStatus
    * * Display Name: Generation Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Read
    * * Value List Type: List
    * * Possible Values 
    *   * Creating Reply
    *   * Failed
    *   * Generated
    *   * Manual
    *   * Read
    *   * Skipped
    * * Description: AI generation status: Read, Creating Reply, Generated, Skipped, Failed
    */
    get GenerationStatus(): 'Creating Reply' | 'Failed' | 'Generated' | 'Manual' | 'Read' | 'Skipped' {
        return this.Get('GenerationStatus');
    }
    set GenerationStatus(value: 'Creating Reply' | 'Failed' | 'Generated' | 'Manual' | 'Read' | 'Skipped') {
        this.Set('GenerationStatus', value);
    }

    /**
    * * Field Name: AIAgentRunID
    * * Display Name: AI Agent Run
    * * SQL Data Type: uniqueidentifier
    */
    get AIAgentRunID(): string | null {
        return this.Get('AIAgentRunID');
    }
    set AIAgentRunID(value: string | null) {
        this.Set('AIAgentRunID', value);
    }

    /**
    * * Field Name: InboundVectorJSON
    * * Display Name: Inbound Vector
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Vector embedding of inbound message for similarity search
    */
    get InboundVectorJSON(): string | null {
        return this.Get('InboundVectorJSON');
    }
    set InboundVectorJSON(value: string | null) {
        this.Set('InboundVectorJSON', value);
    }

    /**
    * * Field Name: ReplyVectorJSON
    * * Display Name: Reply Vector
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Vector embedding of reply for similarity search
    */
    get ReplyVectorJSON(): string | null {
        return this.Get('ReplyVectorJSON');
    }
    set ReplyVectorJSON(value: string | null) {
        this.Set('ReplyVectorJSON', value);
    }

    /**
    * * Field Name: VectorModelID
    * * Display Name: Vector Model
    * * SQL Data Type: uniqueidentifier
    */
    get VectorModelID(): string | null {
        return this.Get('VectorModelID');
    }
    set VectorModelID(value: string | null) {
        this.Set('VectorModelID', value);
    }

    /**
    * * Field Name: ApprovalStatus
    * * Display Name: Approval Status
    * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * Approved
    *   * Escalated
    *   * Not Required
    *   * Pending
    *   * Rejected
    * * Description: Approval status: Pending, Approved, Rejected, Not Required
    */
    get ApprovalStatus(): 'Approved' | 'Escalated' | 'Not Required' | 'Pending' | 'Rejected' | null {
        return this.Get('ApprovalStatus');
    }
    set ApprovalStatus(value: 'Approved' | 'Escalated' | 'Not Required' | 'Pending' | 'Rejected' | null) {
        this.Set('ApprovalStatus', value);
    }

    /**
    * * Field Name: ApprovalFeedback
    * * Display Name: Approval Feedback
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Feedback from approver on why approved/rejected
    */
    get ApprovalFeedback(): string | null {
        return this.Get('ApprovalFeedback');
    }
    set ApprovalFeedback(value: string | null) {
        this.Set('ApprovalFeedback', value);
    }

    /**
    * * Field Name: GeneratedReplyContent
    * * Display Name: Generated Reply
    * * SQL Data Type: nvarchar(MAX)
    * * Description: AI-generated reply content
    */
    get GeneratedReplyContent(): string | null {
        return this.Get('GeneratedReplyContent');
    }
    set GeneratedReplyContent(value: string | null) {
        this.Set('GeneratedReplyContent', value);
    }

    /**
    * * Field Name: ApprovedReplyContent
    * * Display Name: Approved Reply
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Final approved reply content (may be edited from generated)
    */
    get ApprovedReplyContent(): string | null {
        return this.Get('ApprovedReplyContent');
    }
    set ApprovedReplyContent(value: string | null) {
        this.Set('ApprovedReplyContent', value);
    }

    /**
    * * Field Name: ApprovedAt
    * * Display Name: Approved At
    * * SQL Data Type: datetimeoffset
    * * Description: When the reply was approved
    */
    get ApprovedAt(): Date | null {
        return this.Get('ApprovedAt');
    }
    set ApprovedAt(value: Date | null) {
        this.Set('ApprovedAt', value);
    }

    /**
    * * Field Name: ApprovedByPersonID
    * * Display Name: Approved By
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get ApprovedByPersonID(): string | null {
        return this.Get('ApprovedByPersonID');
    }
    set ApprovedByPersonID(value: string | null) {
        this.Set('ApprovedByPersonID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: GeneratedAt
    * * Display Name: Generated At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp when Izzy finished generating the response for this message
    */
    get GeneratedAt(): Date | null {
        return this.Get('GeneratedAt');
    }
    set GeneratedAt(value: Date | null) {
        this.Set('GeneratedAt', value);
    }

    /**
    * * Field Name: SentAt
    * * Display Name: Sent At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp when the response was actually sent to the recipient
    */
    get SentAt(): Date | null {
        return this.Get('SentAt');
    }
    set SentAt(value: Date | null) {
        this.Set('SentAt', value);
    }

    /**
    * * Field Name: AIConfidenceScore
    * * Display Name: AI Confidence Score
    * * SQL Data Type: decimal(5, 2)
    * * Description: AI-generated confidence score for the response quality (0-100 percentage)
    */
    get AIConfidenceScore(): number | null {
        return this.Get('AIConfidenceScore');
    }
    set AIConfidenceScore(value: number | null) {
        this.Set('AIConfidenceScore', value);
    }

    /**
    * * Field Name: AIConfidenceReason
    * * Display Name: AI Confidence Reason
    * * SQL Data Type: nvarchar(MAX)
    * * Description: AI-generated explanation for the confidence score, including any concerns or areas of uncertainty
    */
    get AIConfidenceReason(): string | null {
        return this.Get('AIConfidenceReason');
    }
    set AIConfidenceReason(value: string | null) {
        this.Set('AIConfidenceReason', value);
    }

    /**
    * * Field Name: AIAutoApproved
    * * Display Name: AI Auto Approved
    * * SQL Data Type: bit
    * * Description: Whether this message was automatically approved based on confidence score meeting or exceeding the threshold
    */
    get AIAutoApproved(): boolean | null {
        return this.Get('AIAutoApproved');
    }
    set AIAutoApproved(value: boolean | null) {
        this.Set('AIAutoApproved', value);
    }

    /**
    * * Field Name: AIEvaluatedAt
    * * Display Name: AI Evaluated At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp when the AI confidence evaluation was performed
    */
    get AIEvaluatedAt(): Date | null {
        return this.Get('AIEvaluatedAt');
    }
    set AIEvaluatedAt(value: Date | null) {
        this.Set('AIEvaluatedAt', value);
    }

    /**
    * * Field Name: MessageFormat
    * * Display Name: Message Format
    * * SQL Data Type: nvarchar(20)
    * * Description: Format of the inbound message content. Values: HTML, PlainText, Markdown. Used to determine reply format.
    */
    get MessageFormat(): string | null {
        return this.Get('MessageFormat');
    }
    set MessageFormat(value: string | null) {
        this.Set('MessageFormat', value);
    }

    /**
    * * Field Name: SkipCategory
    * * Display Name: Skip Category
    * * SQL Data Type: nvarchar(100)
    * * Description: Category indicating why the message was skipped. From early rejection: spam, automated-message, prank, phishing. From decision rejection: legal-advice, financial-dispute, explicit-human-request, urgent-sensitive-personal, urgent-sensitive-business, policy-exception, complex-technical, password-reset-failure, marketing-speaker, inappropriate, out-of-scope
    */
    get SkipCategory(): string | null {
        return this.Get('SkipCategory');
    }
    set SkipCategory(value: string | null) {
        this.Set('SkipCategory', value);
    }

    /**
    * * Field Name: SkipReasoning
    * * Display Name: Skip Reasoning
    * * SQL Data Type: nvarchar(MAX)
    * * Description: AI-generated reasoning explaining why the message was skipped or rejected
    */
    get SkipReasoning(): string | null {
        return this.Get('SkipReasoning');
    }
    set SkipReasoning(value: string | null) {
        this.Set('SkipReasoning', value);
    }

    /**
    * * Field Name: IsSpam
    * * Display Name: Is Spam
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Flag indicating the message was identified as spam. Used to filter spam out of pending message lists.
    */
    get IsSpam(): boolean {
        return this.Get('IsSpam');
    }
    set IsSpam(value: boolean) {
        this.Set('IsSpam', value);
    }

    /**
    * * Field Name: ErrorType
    * * Display Name: Error Type
    * * SQL Data Type: nvarchar(50)
    * * Value List Type: List
    * * Possible Values 
    *   * AgentFailure
    *   * ChannelConfigFailure
    *   * CredentialFailure
    *   * Other
    *   * ProviderNotFound
    *   * RateLimited
    *   * SendFailure
    *   * Timeout
    *   * ValidationFailure
    * * Description: Type of error that occurred during processing or sending. Values: AgentFailure, SendFailure, CredentialFailure, ProviderNotFound, ValidationFailure, ChannelConfigFailure, Timeout, RateLimited, Other
    */
    get ErrorType(): 'AgentFailure' | 'ChannelConfigFailure' | 'CredentialFailure' | 'Other' | 'ProviderNotFound' | 'RateLimited' | 'SendFailure' | 'Timeout' | 'ValidationFailure' | null {
        return this.Get('ErrorType');
    }
    set ErrorType(value: 'AgentFailure' | 'ChannelConfigFailure' | 'CredentialFailure' | 'Other' | 'ProviderNotFound' | 'RateLimited' | 'SendFailure' | 'Timeout' | 'ValidationFailure' | null) {
        this.Set('ErrorType', value);
    }

    /**
    * * Field Name: ErrorMessage
    * * Display Name: Error Message
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Detailed error message describing what went wrong during processing or sending. Useful for debugging and support.
    */
    get ErrorMessage(): string | null {
        return this.Get('ErrorMessage');
    }
    set ErrorMessage(value: string | null) {
        this.Set('ErrorMessage', value);
    }

    /**
    * * Field Name: SentContent
    * * Display Name: Sent Content
    * * SQL Data Type: nvarchar(MAX)
    * * Description: The exact content that was sent to the recipient, including any signatures and branding. Populated after successful send for audit trail.
    */
    get SentContent(): string | null {
        return this.Get('SentContent');
    }
    set SentContent(value: string | null) {
        this.Set('SentContent', value);
    }

    /**
    * * Field Name: ForcedGeneration
    * * Display Name: Forced Generation
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Flag indicating the message was force-generated from Escalated status. Prevents auto-approval for safety.
    */
    get ForcedGeneration(): boolean {
        return this.Get('ForcedGeneration');
    }
    set ForcedGeneration(value: boolean) {
        this.Set('ForcedGeneration', value);
    }

    /**
    * * Field Name: EscalatedAt
    * * Display Name: Escalated At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp when the message was escalated (either automatically by filtering or manually by user)
    */
    get EscalatedAt(): Date | null {
        return this.Get('EscalatedAt');
    }
    set EscalatedAt(value: Date | null) {
        this.Set('EscalatedAt', value);
    }

    /**
    * * Field Name: EscalatedByPersonID
    * * Display Name: Escalated By
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Contact who manually escalated the message. NULL for automatic escalations by filtering agent.
    */
    get EscalatedByPersonID(): string | null {
        return this.Get('EscalatedByPersonID');
    }
    set EscalatedByPersonID(value: string | null) {
        this.Set('EscalatedByPersonID', value);
    }

    /**
    * * Field Name: AssignedToPersonID
    * * Display Name: Assigned To
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Contact assigned to review/handle this message
    */
    get AssignedToPersonID(): string | null {
        return this.Get('AssignedToPersonID');
    }
    set AssignedToPersonID(value: string | null) {
        this.Set('AssignedToPersonID', value);
    }

    /**
    * * Field Name: AssignedAt
    * * Display Name: Assigned At
    * * SQL Data Type: datetimeoffset
    * * Description: When the message was assigned to the current contact
    */
    get AssignedAt(): Date | null {
        return this.Get('AssignedAt');
    }
    set AssignedAt(value: Date | null) {
        this.Set('AssignedAt', value);
    }

    /**
    * * Field Name: QualityFeedback
    * * Display Name: Quality Feedback
    * * SQL Data Type: nvarchar(4)
    * * Value List Type: List
    * * Possible Values 
    *   * Down
    *   * Up
    * * Description: Staff quality rating for the AI-generated response. Up = response was helpful and accurate. Down = response had issues. Used for RLHF feedback loops.
    */
    get QualityFeedback(): 'Down' | 'Up' | null {
        return this.Get('QualityFeedback');
    }
    set QualityFeedback(value: 'Down' | 'Up' | null) {
        this.Set('QualityFeedback', value);
    }

    /**
    * * Field Name: QualityFeedbackAt
    * * Display Name: Quality Feedback At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp when the quality feedback was submitted by staff.
    */
    get QualityFeedbackAt(): Date | null {
        return this.Get('QualityFeedbackAt');
    }
    set QualityFeedbackAt(value: Date | null) {
        this.Set('QualityFeedbackAt', value);
    }

    /**
    * * Field Name: QualityFeedbackByPersonID
    * * Display Name: Quality Feedback By
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get QualityFeedbackByPersonID(): string | null {
        return this.Get('QualityFeedbackByPersonID');
    }
    set QualityFeedbackByPersonID(value: string | null) {
        this.Set('QualityFeedbackByPersonID', value);
    }

    /**
    * * Field Name: MessageCategory
    * * Display Name: Message Category
    * * SQL Data Type: nvarchar(100)
    * * Description: AI-assigned category for the inbound message, selected from the organization's predefined MessageCategories setting. Used for mandatory reporting on message topic distribution.
    */
    get MessageCategory(): string | null {
        return this.Get('MessageCategory');
    }
    set MessageCategory(value: string | null) {
        this.Set('MessageCategory', value);
    }

    /**
    * * Field Name: MessageContentPreview
    * * Display Name: Content Preview
    * * SQL Data Type: nvarchar(500)
    * * Description: HTML-stripped, truncated preview of MessageContent for fast list-view rendering. Populated at write time and backfilled for existing rows.
    */
    get MessageContentPreview(): string | null {
        return this.Get('MessageContentPreview');
    }
    set MessageContentPreview(value: string | null) {
        this.Set('MessageContentPreview', value);
    }

    /**
    * * Field Name: IssueID
    * * Display Name: Issue
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
    * * Description: Optional FK to the bizapps-issues Issue (ticket) this message belongs to. NULL if ticket management is not enabled or the message is unlinked. Multiple messages across different channels and threads link to the same Issue — this is the cross-channel ticket link.
    */
    get IssueID(): string | null {
        return this.Get('IssueID');
    }
    set IssueID(value: string | null) {
        this.Set('IssueID', value);
    }

    /**
    * * Field Name: ToRecipients
    * * Display Name: To Recipients
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Inbound only: all To recipients of the message as received, comma-delimited bare email addresses (includes the channel's own address; suppression happens at send time, not capture time). NULL when the provider does not supply recipient lists and on outbound rows.
    */
    get ToRecipients(): string | null {
        return this.Get('ToRecipients');
    }
    set ToRecipients(value: string | null) {
        this.Set('ToRecipients', value);
    }

    /**
    * * Field Name: CCRecipients
    * * Display Name: CC Recipients
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Inbound only: all CC recipients of the message as received, comma-delimited bare email addresses. NULL when there was no CC, when the provider does not supply recipient lists, and on outbound rows.
    */
    get CCRecipients(): string | null {
        return this.Get('CCRecipients');
    }
    set CCRecipients(value: string | null) {
        this.Set('CCRecipients', value);
    }

    /**
    * * Field Name: SentToRecipients
    * * Display Name: Sent To Recipients
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Outbound only: the To recipients Izzy actually sent this message to, comma-delimited, recorded at send time for audit. NULL on inbound rows.
    */
    get SentToRecipients(): string | null {
        return this.Get('SentToRecipients');
    }
    set SentToRecipients(value: string | null) {
        this.Set('SentToRecipients', value);
    }

    /**
    * * Field Name: SentCCRecipients
    * * Display Name: Sent CC Recipients
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Outbound only: the CC recipients Izzy actually sent this message to after suppression and caps, comma-delimited, recorded at send time for audit. NULL on inbound rows and when nothing was CC'd.
    */
    get SentCCRecipients(): string | null {
        return this.Get('SentCCRecipients');
    }
    set SentCCRecipients(value: string | null) {
        this.Set('SentCCRecipients', value);
    }

    /**
    * * Field Name: OutboundType
    * * Display Name: Outbound Type
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * Forward
    *   * New
    *   * Reply
    * * Description: Outbound only: Reply, Forward, or New (staff-composed fresh outbound with no inbound origin). Distinguishes outbound kinds on the ticket timeline. Stamped at creation time for staff-originated rows (a non-null value with SentAt null is a staff draft awaiting approval) and at send time for AI replies. NULL on inbound rows and on outbound rows created before this feature (rendered as replies).
    */
    get OutboundType(): 'Forward' | 'New' | 'Reply' | null {
        return this.Get('OutboundType');
    }
    set OutboundType(value: 'Forward' | 'New' | 'Reply' | null) {
        this.Set('OutboundType', value);
    }

    /**
    * * Field Name: SentByPersonID
    * * Display Name: Sent By
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    */
    get SentByPersonID(): string | null {
        return this.Get('SentByPersonID');
    }
    set SentByPersonID(value: string | null) {
        this.Set('SentByPersonID', value);
    }

    /**
    * * Field Name: MessageContentText
    * * Display Name: Message Content (Plain Text)
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Plain-text rendering of MessageContent: HTML stripped, entities decoded, whitespace collapsed, capped at 32,000 characters. Written whenever MessageContent changes and indexed for full-text search; never shown to users. NULL on rows saved before the backfill.
    */
    get MessageContentText(): string | null {
        return this.Get('MessageContentText');
    }
    set MessageContentText(value: string | null) {
        this.Set('MessageContentText', value);
    }

    /**
    * * Field Name: Channel
    * * Display Name: Channel Name
    * * SQL Data Type: nvarchar(255)
    */
    get Channel(): string {
        return this.Get('Channel');
    }

    /**
    * * Field Name: Parent
    * * Display Name: Parent Message
    * * SQL Data Type: nvarchar(500)
    */
    get Parent(): string | null {
        return this.Get('Parent');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person Name
    * * SQL Data Type: nvarchar(250)
    */
    get Person(): string | null {
        return this.Get('Person');
    }

    /**
    * * Field Name: ApprovedByPerson
    * * Display Name: Approved By Name
    * * SQL Data Type: nvarchar(250)
    */
    get ApprovedByPerson(): string | null {
        return this.Get('ApprovedByPerson');
    }

    /**
    * * Field Name: EscalatedByPerson
    * * Display Name: Escalated By Name
    * * SQL Data Type: nvarchar(250)
    */
    get EscalatedByPerson(): string | null {
        return this.Get('EscalatedByPerson');
    }

    /**
    * * Field Name: AssignedToPerson
    * * Display Name: Assigned To Name
    * * SQL Data Type: nvarchar(250)
    */
    get AssignedToPerson(): string | null {
        return this.Get('AssignedToPerson');
    }

    /**
    * * Field Name: QualityFeedbackByPerson
    * * Display Name: Quality Feedback By Name
    * * SQL Data Type: nvarchar(250)
    */
    get QualityFeedbackByPerson(): string | null {
        return this.Get('QualityFeedbackByPerson');
    }

    /**
    * * Field Name: Issue
    * * Display Name: Issue
    * * SQL Data Type: nvarchar(50)
    */
    get Issue(): string | null {
        return this.Get('Issue');
    }

    /**
    * * Field Name: SentByPerson
    * * Display Name: Sent By Name
    * * SQL Data Type: nvarchar(250)
    */
    get SentByPerson(): string | null {
        return this.Get('SentByPerson');
    }

    /**
    * * Field Name: RootParentID
    * * Display Name: Root Parent
    * * SQL Data Type: uniqueidentifier
    */
    get RootParentID(): string | null {
        return this.Get('RootParentID');
    }

    /**
    * * Field Name: ParentIDDepth
    * * Display Name: Parent Depth
    * * SQL Data Type: int
    */
    get ParentIDDepth(): number | null {
        return this.Get('ParentIDDepth');
    }

    /**
    * * Field Name: ParentIDPath
    * * Display Name: Parent Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get ParentIDPath(): string | null {
        return this.Get('ParentIDPath');
    }

    /**
    * * Field Name: ParentIDIsLeaf
    * * Display Name: Is Leaf
    * * SQL Data Type: bit
    */
    get ParentIDIsLeaf(): boolean | null {
        return this.Get('ParentIDIsLeaf');
    }

    /**
    * * Field Name: ParentIDChildCount
    * * Display Name: Child Count
    * * SQL Data Type: int
    */
    get ParentIDChildCount(): number | null {
        return this.Get('ParentIDChildCount');
    }
}


/**
 * Channel Type Actions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: ChannelTypeAction
 * * Base View: vwChannelTypeActions
 * * @description Channel type action overrides (middle of inheritance)
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Channel Type Actions')
export class IzzyChannelTypeActionEntity extends BaseEntity<IzzyChannelTypeActionEntityType> {
    /**
    * Loads the Channel Type Actions record from the database
    * @param ID: string - primary key value to load the Channel Type Actions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyChannelTypeActionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: ChannelTypeID
    * * Display Name: Channel Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: Channel Types (vwChannelTypes.ID)
    */
    get ChannelTypeID(): string {
        return this.Get('ChannelTypeID');
    }
    set ChannelTypeID(value: string) {
        this.Set('ChannelTypeID', value);
    }

    /**
    * * Field Name: ActionID
    * * Display Name: Action ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)
    */
    get ActionID(): string {
        return this.Get('ActionID');
    }
    set ActionID(value: string) {
        this.Set('ActionID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Disabled
    * * Description: Action status at channel type level: Active, Disabled
    */
    get Status(): 'Active' | 'Disabled' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Disabled') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: Sequence
    * * Display Name: Sequence
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Execution order when multiple actions are available
    */
    get Sequence(): number {
        return this.Get('Sequence');
    }
    set Sequence(value: number) {
        this.Set('Sequence', value);
    }

    /**
    * * Field Name: Configuration
    * * Display Name: Configuration
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON configuration for this action at channel type level
    */
    get Configuration(): string | null {
        return this.Get('Configuration');
    }
    set Configuration(value: string | null) {
        this.Set('Configuration', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CredentialID
    * * Display Name: Credential ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
    * * Description: Reference to the credential used for this action at channel type level
    */
    get CredentialID(): string | null {
        return this.Get('CredentialID');
    }
    set CredentialID(value: string | null) {
        this.Set('CredentialID', value);
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: ChannelType
    * * Display Name: Channel Type
    * * SQL Data Type: nvarchar(255)
    */
    get ChannelType(): string {
        return this.Get('ChannelType');
    }

    /**
    * * Field Name: Action
    * * Display Name: Action
    * * SQL Data Type: nvarchar(425)
    */
    get Action(): string {
        return this.Get('Action');
    }

    /**
    * * Field Name: Credential
    * * Display Name: Credential
    * * SQL Data Type: nvarchar(200)
    */
    get Credential(): string | null {
        return this.Get('Credential');
    }
}


/**
 * Channel Type Extensions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: ChannelTypeExtension
 * * Base View: vwChannelTypeExtensions
 * * @description Izzy-specific extension columns for __BCSaaS.ChannelType. 1:1 by ChannelTypeID.
 * * Primary Key: ChannelTypeID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Channel Type Extensions')
export class IzzyChannelTypeExtensionEntity extends BaseEntity<IzzyChannelTypeExtensionEntityType> {
    /**
    * Loads the Channel Type Extensions record from the database
    * @param ChannelTypeID: string - primary key value to load the Channel Type Extensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyChannelTypeExtensionEntity
    * @method
    * @override
    */
    public async Load(ChannelTypeID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ChannelTypeID', Value: ChannelTypeID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ChannelTypeID
    * * Display Name: Channel Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: Channel Types (vwChannelTypes.ID)
    */
    get ChannelTypeID(): string {
        return this.Get('ChannelTypeID');
    }
    set ChannelTypeID(value: string) {
        this.Set('ChannelTypeID', value);
    }

    /**
    * * Field Name: ActionInheritMode
    * * Display Name: Action Inherit Mode
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Combined
    * * Value List Type: List
    * * Possible Values 
    *   * ChannelType
    *   * Combined
    *   * Organization
    */
    get ActionInheritMode(): 'ChannelType' | 'Combined' | 'Organization' {
        return this.Get('ActionInheritMode');
    }
    set ActionInheritMode(value: 'ChannelType' | 'Combined' | 'Organization') {
        this.Set('ActionInheritMode', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: ChannelType
    * * Display Name: Channel Type
    * * SQL Data Type: nvarchar(255)
    */
    get ChannelType(): string {
        return this.Get('ChannelType');
    }
}


/**
 * Credential Extensions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: CredentialExtension
 * * Base View: vwCredentialExtensions
 * * @description Izzy-specific extension data for MJ Credential records
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Credential Extensions')
export class IzzyCredentialExtensionEntity extends BaseEntity<IzzyCredentialExtensionEntityType> {
    /**
    * Loads the Credential Extensions record from the database
    * @param ID: string - primary key value to load the Credential Extensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyCredentialExtensionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CredentialID
    * * Display Name: Credential
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
    */
    get CredentialID(): string {
        return this.Get('CredentialID');
    }
    set CredentialID(value: string) {
        this.Set('CredentialID', value);
    }

    /**
    * * Field Name: CredentialNotes
    * * Display Name: Notes
    * * SQL Data Type: nvarchar(500)
    * * Description: Freeform notes about this credential
    */
    get CredentialNotes(): string | null {
        return this.Get('CredentialNotes');
    }
    set CredentialNotes(value: string | null) {
        this.Set('CredentialNotes', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Credential
    * * Display Name: Credential Name
    * * SQL Data Type: nvarchar(200)
    */
    get Credential(): string {
        return this.Get('Credential');
    }
}


/**
 * Credential Type Extensions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: CredentialTypeExtension
 * * Base View: vwCredentialTypeExtensions
 * * @description Izzy-specific extension data for MJ CredentialType records
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Credential Type Extensions')
export class IzzyCredentialTypeExtensionEntity extends BaseEntity<IzzyCredentialTypeExtensionEntityType> {
    /**
    * Loads the Credential Type Extensions record from the database
    * @param ID: string - primary key value to load the Credential Type Extensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyCredentialTypeExtensionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: CredentialTypeID
    * * Display Name: Credential Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Credential Types (vwCredentialTypes.ID)
    */
    get CredentialTypeID(): string {
        return this.Get('CredentialTypeID');
    }
    set CredentialTypeID(value: string) {
        this.Set('CredentialTypeID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Description: Active or Inactive status for this credential type
    */
    get Status(): string {
        return this.Get('Status');
    }
    set Status(value: string) {
        this.Set('Status', value);
    }

    /**
    * * Field Name: Website
    * * Display Name: Website
    * * SQL Data Type: nvarchar(500)
    * * Description: Provider website URL
    */
    get Website(): string | null {
        return this.Get('Website');
    }
    set Website(value: string | null) {
        this.Set('Website', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Category
    * * Display Name: Category
    * * SQL Data Type: nvarchar(50)
    */
    get Category(): string | null {
        return this.Get('Category');
    }
    set Category(value: string | null) {
        this.Set('Category', value);
    }

    /**
    * * Field Name: CredentialType
    * * Display Name: Credential Type
    * * SQL Data Type: nvarchar(100)
    */
    get CredentialType(): string {
        return this.Get('CredentialType');
    }
}


/**
 * Issue Ticket Numbers - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: IssueTicketNumber
 * * Base View: vwIssueTicketNumbers
 * * @description Per-Issue per-org ticket number (1:1 with Issue). Assigned once on ticket creation via spAssignNextTicketNumber and immutable thereafter. Lives separate from TicketSLAState so a ticket number exists even when SLA is disabled.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Issue Ticket Numbers')
export class IzzyIssueTicketNumberEntity extends BaseEntity<IzzyIssueTicketNumberEntityType> {
    /**
    * Loads the Issue Ticket Numbers record from the database
    * @param ID: string - primary key value to load the Issue Ticket Numbers record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyIssueTicketNumberEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: IssueID
    * * Display Name: Issue
    * * SQL Data Type: uniqueidentifier
    * * Description: The bizapps-issues Issue this ticket number belongs to (UNIQUE — one ticket number per Issue).
    */
    get IssueID(): string {
        return this.Get('IssueID');
    }
    set IssueID(value: string) {
        this.Set('IssueID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: TicketNumber
    * * Display Name: Ticket Number
    * * SQL Data Type: nvarchar(50)
    * * Description: The formatted org-facing ticket number, e.g. 'ACME-0042'. {PREFIX}-{padded seq} as returned by spAssignNextTicketNumber.
    */
    get TicketNumber(): string {
        return this.Get('TicketNumber');
    }
    set TicketNumber(value: string) {
        this.Set('TicketNumber', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }
}


/**
 * Izzy Action Categories - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: IzzyActionCategory
 * * Base View: vwIzzyActionCategories
 * * @description Hierarchical categories for organizing actions in the gallery
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Izzy Action Categories')
export class IzzyIzzyActionCategoryEntity extends BaseEntity<IzzyIzzyActionCategoryEntityType> {
    /**
    * Loads the Izzy Action Categories record from the database
    * @param ID: string - primary key value to load the Izzy Action Categories record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyIzzyActionCategoryEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(100)
    * * Description: Category display name
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    * * Description: Description of actions in this category
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: ParentID
    * * Display Name: Parent ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Izzy Action Categories (vwIzzyActionCategories.ID)
    */
    get ParentID(): string | null {
        return this.Get('ParentID');
    }
    set ParentID(value: string | null) {
        this.Set('ParentID', value);
    }

    /**
    * * Field Name: IconClass
    * * Display Name: Icon Class
    * * SQL Data Type: nvarchar(100)
    * * Description: Font Awesome icon class for UI display
    */
    get IconClass(): string | null {
        return this.Get('IconClass');
    }
    set IconClass(value: string | null) {
        this.Set('IconClass', value);
    }

    /**
    * * Field Name: SortOrder
    * * Display Name: Sort Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Display order within parent category
    */
    get SortOrder(): number {
        return this.Get('SortOrder');
    }
    set SortOrder(value: number) {
        this.Set('SortOrder', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
    * * Description: Category status: Active, Inactive
    */
    get Status(): 'Active' | 'Inactive' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Inactive') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Parent
    * * Display Name: Parent
    * * SQL Data Type: nvarchar(100)
    */
    get Parent(): string | null {
        return this.Get('Parent');
    }

    /**
    * * Field Name: RootParentID
    * * Display Name: Root Parent ID
    * * SQL Data Type: uniqueidentifier
    */
    get RootParentID(): string | null {
        return this.Get('RootParentID');
    }

    /**
    * * Field Name: ParentIDDepth
    * * Display Name: Depth
    * * SQL Data Type: int
    */
    get ParentIDDepth(): number | null {
        return this.Get('ParentIDDepth');
    }

    /**
    * * Field Name: ParentIDPath
    * * Display Name: Path
    * * SQL Data Type: nvarchar(MAX)
    */
    get ParentIDPath(): string | null {
        return this.Get('ParentIDPath');
    }

    /**
    * * Field Name: ParentIDIsLeaf
    * * Display Name: Is Leaf
    * * SQL Data Type: bit
    */
    get ParentIDIsLeaf(): boolean | null {
        return this.Get('ParentIDIsLeaf');
    }

    /**
    * * Field Name: ParentIDChildCount
    * * Display Name: Child Count
    * * SQL Data Type: int
    */
    get ParentIDChildCount(): number | null {
        return this.Get('ParentIDChildCount');
    }
}


/**
 * Izzy Action Organizations - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: IzzyActionOrganization
 * * Base View: vwIzzyActionOrganizations
 * * @description Grants private actions to specific organizations
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Izzy Action Organizations')
export class IzzyIzzyActionOrganizationEntity extends BaseEntity<IzzyIzzyActionOrganizationEntityType> {
    /**
    * Loads the Izzy Action Organizations record from the database
    * @param ID: string - primary key value to load the Izzy Action Organizations record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyIzzyActionOrganizationEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: IzzyActionID
    * * Display Name: Action
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Izzy Actions (vwIzzyActions.ID)
    */
    get IzzyActionID(): string {
        return this.Get('IzzyActionID');
    }
    set IzzyActionID(value: string) {
        this.Set('IzzyActionID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Revoked
    * * Description: Grant status: Active, Revoked
    */
    get Status(): 'Active' | 'Revoked' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Revoked') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: GrantedAt
    * * Display Name: Granted At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    * * Description: When the action was granted to this organization
    */
    get GrantedAt(): Date {
        return this.Get('GrantedAt');
    }
    set GrantedAt(value: Date) {
        this.Set('GrantedAt', value);
    }

    /**
    * * Field Name: ExpiresAt
    * * Display Name: Expires At
    * * SQL Data Type: datetimeoffset
    * * Description: When this grant expires (null = never)
    */
    get ExpiresAt(): Date | null {
        return this.Get('ExpiresAt');
    }
    set ExpiresAt(value: Date | null) {
        this.Set('ExpiresAt', value);
    }

    /**
    * * Field Name: Notes
    * * Display Name: Notes
    * * SQL Data Type: nvarchar(500)
    * * Description: Admin notes about why this was granted
    */
    get Notes(): string | null {
        return this.Get('Notes');
    }
    set Notes(value: string | null) {
        this.Set('Notes', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: IzzyAction
    * * Display Name: Action Name
    * * SQL Data Type: nvarchar(255)
    */
    get IzzyAction(): string {
        return this.Get('IzzyAction');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization Name
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }
}


/**
 * Izzy Actions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: IzzyAction
 * * Base View: vwIzzyActions
 * * @description Master list of actions available in the Izzy platform
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Izzy Actions')
export class IzzyIzzyActionEntity extends BaseEntity<IzzyIzzyActionEntityType> {
    /**
    * Loads the Izzy Actions record from the database
    * @param ID: string - primary key value to load the Izzy Actions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyIzzyActionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: ActionID
    * * Display Name: Action ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)
    */
    get ActionID(): string {
        return this.Get('ActionID');
    }
    set ActionID(value: string) {
        this.Set('ActionID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Display name for the action
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Detailed description of what the action does
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: CategoryID
    * * Display Name: Category
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Izzy Action Categories (vwIzzyActionCategories.ID)
    */
    get CategoryID(): string | null {
        return this.Get('CategoryID');
    }
    set CategoryID(value: string | null) {
        this.Set('CategoryID', value);
    }

    /**
    * * Field Name: IconClass
    * * Display Name: Icon Class
    * * SQL Data Type: nvarchar(100)
    * * Description: Font Awesome icon class for UI display
    */
    get IconClass(): string | null {
        return this.Get('IconClass');
    }
    set IconClass(value: string | null) {
        this.Set('IconClass', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Coming Soon
    *   * Deprecated
    * * Description: Action status: Active, Deprecated, Coming Soon
    */
    get Status(): 'Active' | 'Coming Soon' | 'Deprecated' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Coming Soon' | 'Deprecated') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: IsPublic
    * * Display Name: Is Public
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether this action is available to all orgs (vs private)
    */
    get IsPublic(): boolean {
        return this.Get('IsPublic');
    }
    set IsPublic(value: boolean) {
        this.Set('IsPublic', value);
    }

    /**
    * * Field Name: MinimumPlanLevel
    * * Display Name: Minimum Plan Level
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Minimum plan level required to use this action
    */
    get MinimumPlanLevel(): number {
        return this.Get('MinimumPlanLevel');
    }
    set MinimumPlanLevel(value: number) {
        this.Set('MinimumPlanLevel', value);
    }

    /**
    * * Field Name: RequiresCredentials
    * * Display Name: Requires Credentials
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: Whether this action requires credentials to be configured
    */
    get RequiresCredentials(): boolean {
        return this.Get('RequiresCredentials');
    }
    set RequiresCredentials(value: boolean) {
        this.Set('RequiresCredentials', value);
    }

    /**
    * * Field Name: CredentialSchema
    * * Display Name: Credential Schema
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON Schema defining required credential fields
    */
    get CredentialSchema(): string | null {
        return this.Get('CredentialSchema');
    }
    set CredentialSchema(value: string | null) {
        this.Set('CredentialSchema', value);
    }

    /**
    * * Field Name: ConfigurationSchema
    * * Display Name: Configuration Schema
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON Schema defining configuration options
    */
    get ConfigurationSchema(): string | null {
        return this.Get('ConfigurationSchema');
    }
    set ConfigurationSchema(value: string | null) {
        this.Set('ConfigurationSchema', value);
    }

    /**
    * * Field Name: DocumentationURL
    * * Display Name: Documentation URL
    * * SQL Data Type: nvarchar(1024)
    * * Description: URL to action documentation
    */
    get DocumentationURL(): string | null {
        return this.Get('DocumentationURL');
    }
    set DocumentationURL(value: string | null) {
        this.Set('DocumentationURL', value);
    }

    /**
    * * Field Name: SortOrder
    * * Display Name: Sort Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Display order within category
    */
    get SortOrder(): number {
        return this.Get('SortOrder');
    }
    set SortOrder(value: number) {
        this.Set('SortOrder', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Action
    * * Display Name: Action
    * * SQL Data Type: nvarchar(425)
    */
    get Action(): string {
        return this.Get('Action');
    }

    /**
    * * Field Name: Category
    * * Display Name: Category
    * * SQL Data Type: nvarchar(100)
    */
    get Category(): string | null {
        return this.Get('Category');
    }
}


/**
 * Macros - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: Macro
 * * Base View: vwMacros
 * * @description A reusable canned reply (PRD v4.0 §11.6). Agents insert a macro into the ticket-detail reply composer via /macro search; insertion drops its Content (with placeholders resolved) and applies its FieldChanges. Scoped Personal/Team/Org.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Macros')
export class IzzyMacroEntity extends BaseEntity<IzzyMacroEntityType> {
    /**
    * Loads the Macros record from the database
    * @param ID: string - primary key value to load the Macros record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyMacroEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization (bizapps-common Organization) the macro belongs to.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: OwnerPersonID
    * * Display Name: Owner Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The owning agent (bizapps-common Person) for a Personal-scope macro; NULL for shared (Team/Org) macros.
    */
    get OwnerPersonID(): string | null {
        return this.Get('OwnerPersonID');
    }
    set OwnerPersonID(value: string | null) {
        this.Set('OwnerPersonID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Short name shown in the /macro picker.
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Content
    * * Display Name: Content
    * * SQL Data Type: nvarchar(MAX)
    * * Description: The reply body inserted when the macro is selected; may contain {{placeholders}} like {{requester.FirstName}}, {{ticket.IssueNumber}}, {{agent.FirstName}}, {{organization.Name}}.
    */
    get Content(): string {
        return this.Get('Content');
    }
    set Content(value: string) {
        this.Set('Content', value);
    }

    /**
    * * Field Name: ContentFormat
    * * Display Name: Content Format
    * * SQL Data Type: nvarchar(10)
    * * Default Value: HTML
    * * Value List Type: List
    * * Possible Values 
    *   * HTML
    *   * Text
    * * Description: Format of Content: HTML or Text.
    */
    get ContentFormat(): 'HTML' | 'Text' {
        return this.Get('ContentFormat');
    }
    set ContentFormat(value: 'HTML' | 'Text') {
        this.Set('ContentFormat', value);
    }

    /**
    * * Field Name: FieldChanges
    * * Display Name: Field Changes
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Optional JSON field changes applied to the ticket when the macro is used, e.g. { "Status": "Resolved", "Priority": "High" }.
    */
    get FieldChanges(): string | null {
        return this.Get('FieldChanges');
    }
    set FieldChanges(value: string | null) {
        this.Set('FieldChanges', value);
    }

    /**
    * * Field Name: Scope
    * * Display Name: Scope
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Org
    * * Value List Type: List
    * * Possible Values 
    *   * Org
    *   * Personal
    *   * Team
    * * Description: Visibility scope: Personal (only OwnerPersonID), Team (only TeamID members), or Org (everyone in the org).
    */
    get Scope(): 'Org' | 'Personal' | 'Team' {
        return this.Get('Scope');
    }
    set Scope(value: 'Org' | 'Personal' | 'Team') {
        this.Set('Scope', value);
    }

    /**
    * * Field Name: TeamID
    * * Display Name: Team ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Ticket Teams (vwTicketTeams.ID)
    * * Description: The team (izzy.TicketTeam) for a Team-scope macro.
    */
    get TeamID(): string | null {
        return this.Get('TeamID');
    }
    set TeamID(value: string | null) {
        this.Set('TeamID', value);
    }

    /**
    * * Field Name: UsageCount
    * * Display Name: Usage Count
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: How many times the macro has been used (incremented on insertion).
    */
    get UsageCount(): number {
        return this.Get('UsageCount');
    }
    set UsageCount(value: number) {
        this.Set('UsageCount', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
    * * Description: Whether the macro is active and offered in the picker.
    */
    get Status(): 'Active' | 'Inactive' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Inactive') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: OwnerPerson
    * * Display Name: Owner Person
    * * SQL Data Type: nvarchar(250)
    */
    get OwnerPerson(): string | null {
        return this.Get('OwnerPerson');
    }

    /**
    * * Field Name: Team
    * * Display Name: Team
    * * SQL Data Type: nvarchar(255)
    */
    get Team(): string | null {
        return this.Get('Team');
    }
}


/**
 * Organization Actions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: OrganizationAction
 * * Base View: vwOrganizationActions
 * * @description Organization-level action configuration (top of inheritance)
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Organization Actions')
export class IzzyOrganizationActionEntity extends BaseEntity<IzzyOrganizationActionEntityType> {
    /**
    * Loads the Organization Actions record from the database
    * @param ID: string - primary key value to load the Organization Actions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyOrganizationActionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: ActionID
    * * Display Name: Action
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Actions (vwActions.ID)
    */
    get ActionID(): string {
        return this.Get('ActionID');
    }
    set ActionID(value: string) {
        this.Set('ActionID', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Disabled
    * * Description: Action status at org level: Active, Disabled
    */
    get Status(): 'Active' | 'Disabled' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Disabled') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: Sequence
    * * Display Name: Sequence
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Execution order when multiple actions are available
    */
    get Sequence(): number {
        return this.Get('Sequence');
    }
    set Sequence(value: number) {
        this.Set('Sequence', value);
    }

    /**
    * * Field Name: Configuration
    * * Display Name: Configuration
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON configuration for this action at org level
    */
    get Configuration(): string | null {
        return this.Get('Configuration');
    }
    set Configuration(value: string | null) {
        this.Set('Configuration', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: CredentialID
    * * Display Name: Credential ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ: Credentials (vwCredentials.ID)
    * * Description: Reference to the credential used for this action at org level
    */
    get CredentialID(): string | null {
        return this.Get('CredentialID');
    }
    set CredentialID(value: string | null) {
        this.Set('CredentialID', value);
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization Name
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: Action
    * * Display Name: Action Name
    * * SQL Data Type: nvarchar(425)
    */
    get Action(): string {
        return this.Get('Action');
    }

    /**
    * * Field Name: Credential
    * * Display Name: Credential Name
    * * SQL Data Type: nvarchar(200)
    */
    get Credential(): string | null {
        return this.Get('Credential');
    }
}


/**
 * Pending Crawl Results - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: PendingCrawlResult
 * * Base View: vwPendingCrawlResults
 * * @description Stores the output of scheduled auto-crawl runs, pending admin review before being applied to organization settings
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Pending Crawl Results')
export class IzzyPendingCrawlResultEntity extends BaseEntity<IzzyPendingCrawlResultEntityType> {
    /**
    * Loads the Pending Crawl Results record from the database
    * @param ID: string - primary key value to load the Pending Crawl Results record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyPendingCrawlResultEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization this crawl result belongs to
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: CrawledURL
    * * Display Name: Crawled URL
    * * SQL Data Type: nvarchar(2048)
    * * Description: The URL that was crawled
    */
    get CrawledURL(): string {
        return this.Get('CrawledURL');
    }
    set CrawledURL(value: string) {
        this.Set('CrawledURL', value);
    }

    /**
    * * Field Name: CrawledAt
    * * Display Name: Crawled At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    * * Description: Timestamp when the crawl was executed
    */
    get CrawledAt(): Date {
        return this.Get('CrawledAt');
    }
    set CrawledAt(value: Date) {
        this.Set('CrawledAt', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(50)
    * * Default Value: Pending
    * * Value List Type: List
    * * Possible Values 
    *   * Accepted
    *   * Dismissed
    *   * Pending
    * * Description: Review status: Pending = awaiting admin review, Accepted = admin applied changes, Dismissed = admin rejected
    */
    get Status(): 'Accepted' | 'Dismissed' | 'Pending' {
        return this.Get('Status');
    }
    set Status(value: 'Accepted' | 'Dismissed' | 'Pending') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: ResultJSON
    * * Display Name: Result JSON
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Raw JSON output from the Setup KB Generator agent containing proposed setting sections
    */
    get ResultJSON(): string | null {
        return this.Get('ResultJSON');
    }
    set ResultJSON(value: string | null) {
        this.Set('ResultJSON', value);
    }

    /**
    * * Field Name: ReviewedAt
    * * Display Name: Reviewed At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp when an admin reviewed this result
    */
    get ReviewedAt(): Date | null {
        return this.Get('ReviewedAt');
    }
    set ReviewedAt(value: Date | null) {
        this.Set('ReviewedAt', value);
    }

    /**
    * * Field Name: ReviewedByPersonID
    * * Display Name: Reviewed By Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The contact (admin) who reviewed this result
    */
    get ReviewedByPersonID(): string | null {
        return this.Get('ReviewedByPersonID');
    }
    set ReviewedByPersonID(value: string | null) {
        this.Set('ReviewedByPersonID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: ReviewedByPerson
    * * Display Name: Reviewed By Person
    * * SQL Data Type: nvarchar(250)
    */
    get ReviewedByPerson(): string | null {
        return this.Get('ReviewedByPerson');
    }
}


/**
 * Person Skills - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: PersonSkill
 * * Base View: vwPersonSkills
 * * @description A Person's proficiency in a Skill, for skills-based ticket routing (PRD v4.0 §9.4). One row per (Person, Skill).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Person Skills')
export class IzzyPersonSkillEntity extends BaseEntity<IzzyPersonSkillEntityType> {
    /**
    * Loads the Person Skills record from the database
    * @param ID: string - primary key value to load the Person Skills record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyPersonSkillEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Person Skills entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * ProficiencyLevel: The proficiency level must be a value between 1 and 5 inclusive to ensure standardized skill assessment.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateProficiencyLevelRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The proficiency level must be a value between 1 and 5 inclusive to ensure standardized skill assessment.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateProficiencyLevelRange(result: ValidationResult) {
    	if (this.ProficiencyLevel != null && (this.ProficiencyLevel < 1 || this.ProficiencyLevel > 5)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"ProficiencyLevel",
    			"Proficiency level must be an integer between 1 and 5.",
    			this.ProficiencyLevel,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The agent (bizapps-common Person) who holds the skill.
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: SkillID
    * * Display Name: Skill ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Skills (vwSkills.ID)
    * * Description: The skill held (izzy.Skill).
    */
    get SkillID(): string {
        return this.Get('SkillID');
    }
    set SkillID(value: string) {
        this.Set('SkillID', value);
    }

    /**
    * * Field Name: ProficiencyLevel
    * * Display Name: Proficiency Level
    * * SQL Data Type: int
    * * Default Value: 3
    * * Description: Proficiency from 1 (novice) to 5 (expert); used to score agents for skills-based routing.
    */
    get ProficiencyLevel(): number {
        return this.Get('ProficiencyLevel');
    }
    set ProficiencyLevel(value: number) {
        this.Set('ProficiencyLevel', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person
    * * SQL Data Type: nvarchar(250)
    */
    get Person(): string {
        return this.Get('Person');
    }

    /**
    * * Field Name: Skill
    * * Display Name: Skill
    * * SQL Data Type: nvarchar(255)
    */
    get Skill(): string {
        return this.Get('Skill');
    }
}


/**
 * SLA Policies - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: SLAPolicy
 * * Base View: vwSLAPolicies
 * * @description A per-organization SLA target set, matched to a ticket by Priority. Defines first-response and resolution time targets and the escalation path when breached.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'SLA Policies')
export class IzzySLAPolicyEntity extends BaseEntity<IzzySLAPolicyEntityType> {
    /**
    * Loads the SLA Policies record from the database
    * @param ID: string - primary key value to load the SLA Policies record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzySLAPolicyEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization that owns this SLA policy.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Display name of the policy (e.g. "Critical-priority tickets").
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Priority
    * * Display Name: Priority
    * * SQL Data Type: nvarchar(20)
    * * Value List Type: List
    * * Possible Values 
    *   * All
    *   * Critical
    *   * High
    *   * Low
    *   * Medium
    * * Description: Ticket priority this policy applies to: Low/Medium/High/Critical, or All to match any priority. The most specific matching policy (by SortOrder) wins.
    */
    get Priority(): 'All' | 'Critical' | 'High' | 'Low' | 'Medium' {
        return this.Get('Priority');
    }
    set Priority(value: 'All' | 'Critical' | 'High' | 'Low' | 'Medium') {
        this.Set('Priority', value);
    }

    /**
    * * Field Name: FirstResponseTargetMinutes
    * * Display Name: First Response Target (Minutes)
    * * SQL Data Type: int
    * * Description: Target time, in minutes, from ticket creation to first agent response. NULL means no first-response target.
    */
    get FirstResponseTargetMinutes(): number | null {
        return this.Get('FirstResponseTargetMinutes');
    }
    set FirstResponseTargetMinutes(value: number | null) {
        this.Set('FirstResponseTargetMinutes', value);
    }

    /**
    * * Field Name: ResolutionTargetMinutes
    * * Display Name: Resolution Target (Minutes)
    * * SQL Data Type: int
    * * Description: Target time, in minutes, from ticket creation to resolution. NULL means no resolution target.
    */
    get ResolutionTargetMinutes(): number | null {
        return this.Get('ResolutionTargetMinutes');
    }
    set ResolutionTargetMinutes(value: number | null) {
        this.Set('ResolutionTargetMinutes', value);
    }

    /**
    * * Field Name: BusinessHoursOnly
    * * Display Name: Business Hours Only
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: When 1, SLA timers count only business hours (per the org business-hours configuration); when 0, they count calendar time.
    */
    get BusinessHoursOnly(): boolean {
        return this.Get('BusinessHoursOnly');
    }
    set BusinessHoursOnly(value: boolean) {
        this.Set('BusinessHoursOnly', value);
    }

    /**
    * * Field Name: EscalationPersonID
    * * Display Name: Escalation Person
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: Optional person to escalate to when the SLA is breached or nears breach.
    */
    get EscalationPersonID(): string | null {
        return this.Get('EscalationPersonID');
    }
    set EscalationPersonID(value: string | null) {
        this.Set('EscalationPersonID', value);
    }

    /**
    * * Field Name: EscalationTeamID
    * * Display Name: Escalation Team
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Ticket Teams (vwTicketTeams.ID)
    * * Description: Optional team to escalate to when the SLA is breached or nears breach.
    */
    get EscalationTeamID(): string | null {
        return this.Get('EscalationTeamID');
    }
    set EscalationTeamID(value: string | null) {
        this.Set('EscalationTeamID', value);
    }

    /**
    * * Field Name: SortOrder
    * * Display Name: Sort Order
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Evaluation order when multiple policies could match; lower numbers are checked first. The first matching active policy is applied.
    */
    get SortOrder(): number {
        return this.Get('SortOrder');
    }
    set SortOrder(value: number) {
        this.Set('SortOrder', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
    * * Description: Lifecycle status: Active = used for matching, Inactive = retained but not applied.
    */
    get Status(): 'Active' | 'Inactive' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Inactive') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization Name
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }

    /**
    * * Field Name: EscalationPerson
    * * Display Name: Escalation Person Name
    * * SQL Data Type: nvarchar(250)
    */
    get EscalationPerson(): string | null {
        return this.Get('EscalationPerson');
    }

    /**
    * * Field Name: EscalationTeam
    * * Display Name: Escalation Team Name
    * * SQL Data Type: nvarchar(255)
    */
    get EscalationTeam(): string | null {
        return this.Get('EscalationTeam');
    }
}


/**
 * Scopes - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: Scope
 * * Base View: vwScopes
 * * @description API permission scopes for access control
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Scopes')
export class IzzyScopeEntity extends BaseEntity<IzzyScopeEntityType> {
    /**
    * Loads the Scopes record from the database
    * @param ID: string - primary key value to load the Scopes record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyScopeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(100)
    * * Description: Scope identifier (e.g., messages:read)
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(500)
    * * Description: Human-readable description of what this scope allows
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }
}


/**
 * Setting Extensions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: SettingExtension
 * * Base View: vwSettingExtensions
 * * @description Izzy-specific metadata extensions for BCSaaS settings
 * * Primary Key: SettingID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Setting Extensions')
export class IzzySettingExtensionEntity extends BaseEntity<IzzySettingExtensionEntityType> {
    /**
    * Loads the Setting Extensions record from the database
    * @param SettingID: string - primary key value to load the Setting Extensions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzySettingExtensionEntity
    * @method
    * @override
    */
    public async Load(SettingID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'SettingID', Value: SettingID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: SettingID
    * * Display Name: Setting ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: BC: Settings (vwSettings.ID)
    */
    get SettingID(): string {
        return this.Get('SettingID');
    }
    set SettingID(value: string) {
        this.Set('SettingID', value);
    }

    /**
    * * Field Name: Icon
    * * Display Name: Icon
    * * SQL Data Type: nvarchar(100)
    * * Description: Icon identifier for the setting
    */
    get Icon(): string | null {
        return this.Get('Icon');
    }
    set Icon(value: string | null) {
        this.Set('Icon', value);
    }

    /**
    * * Field Name: IsCategoryController
    * * Display Name: Is Category Controller
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: When true, this setting controls which category is shown to the user
    */
    get IsCategoryController(): boolean {
        return this.Get('IsCategoryController');
    }
    set IsCategoryController(value: boolean) {
        this.Set('IsCategoryController', value);
    }

    /**
    * * Field Name: ValueType
    * * Display Name: Value Type
    * * SQL Data Type: nvarchar(50)
    * * Description: Expected value type for the setting (e.g. boolean, string, number)
    */
    get ValueType(): string | null {
        return this.Get('ValueType');
    }
    set ValueType(value: string | null) {
        this.Set('ValueType', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: AllowedValues
    * * Display Name: Allowed Values
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Optional JSON array of allowed values for a List-type setting, e.g. ["None","RoundRobin","LeastLoad"]. When ValueType is 'List', the Izzy Settings UI renders a dropdown populated from this array; NULL for non-List settings.
    */
    get AllowedValues(): string | null {
        return this.Get('AllowedValues');
    }
    set AllowedValues(value: string | null) {
        this.Set('AllowedValues', value);
    }

    /**
    * * Field Name: Setting
    * * Display Name: Setting
    * * SQL Data Type: nvarchar(255)
    */
    get Setting(): string {
        return this.Get('Setting');
    }
}


/**
 * Skill Issue Types - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: SkillIssueType
 * * Base View: vwSkillIssueTypes
 * * @description Maps which skill(s) a ticket IssueType requires, for skills-based routing (PRD v4.0 §9.4). A ticket of the given type prefers agents with the listed skills (at or above MinProficiency).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Skill Issue Types')
export class IzzySkillIssueTypeEntity extends BaseEntity<IzzySkillIssueTypeEntityType> {
    /**
    * Loads the Skill Issue Types record from the database
    * @param ID: string - primary key value to load the Skill Issue Types record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzySkillIssueTypeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Skill Issue Types entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * MinProficiency: If specified, the minimum proficiency must be a value between 1 and 5.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateMinProficiencyRange(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * If specified, the minimum proficiency must be a value between 1 and 5.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateMinProficiencyRange(result: ValidationResult) {
    	if (this.MinProficiency != null && (this.MinProficiency < 1 || this.MinProficiency > 5)) {
    		result.Errors.push(new ValidationErrorInfo(
    			"MinProficiency",
    			"Minimum proficiency must be a value between 1 and 5.",
    			this.MinProficiency,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SkillID
    * * Display Name: Skill ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Skills (vwSkills.ID)
    * * Description: The required skill (izzy.Skill).
    */
    get SkillID(): string {
        return this.Get('SkillID');
    }
    set SkillID(value: string) {
        this.Set('SkillID', value);
    }

    /**
    * * Field Name: IssueTypeID
    * * Display Name: Issue Type ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issue Types (vwIssueTypes.ID)
    * * Description: The ticket type (bizapps-issues IssueType) that requires the skill.
    */
    get IssueTypeID(): string {
        return this.Get('IssueTypeID');
    }
    set IssueTypeID(value: string) {
        this.Set('IssueTypeID', value);
    }

    /**
    * * Field Name: MinProficiency
    * * Display Name: Min Proficiency
    * * SQL Data Type: int
    * * Description: Optional minimum proficiency (1-5) an agent must have in the skill to be considered for this type; NULL means any proficiency qualifies.
    */
    get MinProficiency(): number | null {
        return this.Get('MinProficiency');
    }
    set MinProficiency(value: number | null) {
        this.Set('MinProficiency', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Skill
    * * Display Name: Skill
    * * SQL Data Type: nvarchar(255)
    */
    get Skill(): string {
        return this.Get('Skill');
    }

    /**
    * * Field Name: IssueType
    * * Display Name: Issue Type
    * * SQL Data Type: nvarchar(100)
    */
    get IssueType(): string {
        return this.Get('IssueType');
    }
}


/**
 * Skills - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: Skill
 * * Base View: vwSkills
 * * @description An organization-defined skill used for skills-based ticket routing (PRD v4.0 §9.4). Agents hold skills (via PersonSkill) and ticket types require them (via SkillIssueType).
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Skills')
export class IzzySkillEntity extends BaseEntity<IzzySkillEntityType> {
    /**
    * Loads the Skills record from the database
    * @param ID: string - primary key value to load the Skills record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzySkillEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization (bizapps-common Organization) that defines this skill.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Skill name, e.g. "Billing", "API Support", "Spanish".
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(1000)
    * * Description: Optional description of the skill.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
    * * Description: Whether the skill is active. Inactive skills are ignored by routing.
    */
    get Status(): 'Active' | 'Inactive' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Inactive') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }
}


/**
 * Ticket Merges - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: TicketMerge
 * * Base View: vwTicketMerges
 * * @description Audit + reversal record for a staff-initiated tombstone merge: a ticket merged into another and/or a reporter Person merged into another. The MovedManifest captures everything that moved so the merge can be un-done. Losers are tombstoned (ticket -> terminal status; Person -> Inactive), not deleted; this row marks them as merged.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ticket Merges')
export class IzzyTicketMergeEntity extends BaseEntity<IzzyTicketMergeEntityType> {
    /**
    * Loads the Ticket Merges record from the database
    * @param ID: string - primary key value to load the Ticket Merges record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyTicketMergeEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: SurvivorIssueID
    * * Display Name: Survivor Ticket
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
    * * Description: The ticket (bizapps-issues Issue) that remains and absorbs the loser's messages/comments. NULL for a standalone person merge.
    */
    get SurvivorIssueID(): string | null {
        return this.Get('SurvivorIssueID');
    }
    set SurvivorIssueID(value: string | null) {
        this.Set('SurvivorIssueID', value);
    }

    /**
    * * Field Name: LoserIssueID
    * * Display Name: Loser Ticket
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
    * * Description: The ticket (bizapps-issues Issue) merged into the survivor and tombstoned. NULL for a standalone person merge.
    */
    get LoserIssueID(): string | null {
        return this.Get('LoserIssueID');
    }
    set LoserIssueID(value: string | null) {
        this.Set('LoserIssueID', value);
    }

    /**
    * * Field Name: MergedByPersonID
    * * Display Name: Merged By
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The staff Person who performed the merge; NULL for system/automated merges.
    */
    get MergedByPersonID(): string | null {
        return this.Get('MergedByPersonID');
    }
    set MergedByPersonID(value: string | null) {
        this.Set('MergedByPersonID', value);
    }

    /**
    * * Field Name: MergedAt
    * * Display Name: Merged At
    * * SQL Data Type: datetimeoffset
    * * Default Value: sysdatetimeoffset()
    * * Description: When the merge was performed.
    */
    get MergedAt(): Date {
        return this.Get('MergedAt');
    }
    set MergedAt(value: Date) {
        this.Set('MergedAt', value);
    }

    /**
    * * Field Name: PersonMerged
    * * Display Name: Reporter Merge Performed
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: 1 when reporter People were merged into one (consolidating contact methods); 0 for a ticket-only merge.
    */
    get PersonMerged(): boolean {
        return this.Get('PersonMerged');
    }
    set PersonMerged(value: boolean) {
        this.Set('PersonMerged', value);
    }

    /**
    * * Field Name: SurvivorPersonID
    * * Display Name: Survivor Reporter
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The surviving reporter Person when PersonMerged=1; NULL otherwise.
    */
    get SurvivorPersonID(): string | null {
        return this.Get('SurvivorPersonID');
    }
    set SurvivorPersonID(value: string | null) {
        this.Set('SurvivorPersonID', value);
    }

    /**
    * * Field Name: LoserPersonID
    * * Display Name: Loser Reporter
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The merged-away reporter Person (tombstoned Inactive) when PersonMerged=1; NULL otherwise.
    */
    get LoserPersonID(): string | null {
        return this.Get('LoserPersonID');
    }
    set LoserPersonID(value: string | null) {
        this.Set('LoserPersonID', value);
    }

    /**
    * * Field Name: LoserPriorStatusID
    * * Display Name: Loser Prior Status
    * * SQL Data Type: uniqueidentifier
    * * Description: The loser Issue's StatusID immediately before the merge, captured so un-merge can restore it.
    */
    get LoserPriorStatusID(): string | null {
        return this.Get('LoserPriorStatusID');
    }
    set LoserPriorStatusID(value: string | null) {
        this.Set('LoserPriorStatusID', value);
    }

    /**
    * * Field Name: MovedManifest
    * * Display Name: Moved Manifest
    * * SQL Data Type: nvarchar(MAX)
    * * Description: JSON manifest of exactly what the merge moved (re-pointed FK references, and any 1:1 satellite rows deleted, with prior values/snapshots). Replayed in reverse by un-merge.
    */
    get MovedManifest(): string | null {
        return this.Get('MovedManifest');
    }
    set MovedManifest(value: string | null) {
        this.Set('MovedManifest', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Merge Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Merged
    * * Value List Type: List
    * * Possible Values 
    *   * Merged
    *   * Reversed
    * * Description: Merge lifecycle: Merged = in effect; Reversed = un-merged (manifest replayed back).
    */
    get Status(): 'Merged' | 'Reversed' {
        return this.Get('Status');
    }
    set Status(value: 'Merged' | 'Reversed') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: ReversedAt
    * * Display Name: Reversed At
    * * SQL Data Type: datetimeoffset
    * * Description: When the merge was reversed; NULL while still in effect.
    */
    get ReversedAt(): Date | null {
        return this.Get('ReversedAt');
    }
    set ReversedAt(value: Date | null) {
        this.Set('ReversedAt', value);
    }

    /**
    * * Field Name: ReversedByPersonID
    * * Display Name: Reversed By
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The staff Person who reversed the merge; NULL while still in effect.
    */
    get ReversedByPersonID(): string | null {
        return this.Get('ReversedByPersonID');
    }
    set ReversedByPersonID(value: string | null) {
        this.Set('ReversedByPersonID', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: SurvivorIssue
    * * Display Name: Survivor Issue
    * * SQL Data Type: nvarchar(50)
    */
    get SurvivorIssue(): string | null {
        return this.Get('SurvivorIssue');
    }

    /**
    * * Field Name: LoserIssue
    * * Display Name: Loser Issue
    * * SQL Data Type: nvarchar(50)
    */
    get LoserIssue(): string | null {
        return this.Get('LoserIssue');
    }

    /**
    * * Field Name: MergedByPerson
    * * Display Name: Merged By Name
    * * SQL Data Type: nvarchar(250)
    */
    get MergedByPerson(): string | null {
        return this.Get('MergedByPerson');
    }

    /**
    * * Field Name: SurvivorPerson
    * * Display Name: Survivor Reporter Name
    * * SQL Data Type: nvarchar(250)
    */
    get SurvivorPerson(): string | null {
        return this.Get('SurvivorPerson');
    }

    /**
    * * Field Name: LoserPerson
    * * Display Name: Loser Reporter Name
    * * SQL Data Type: nvarchar(250)
    */
    get LoserPerson(): string | null {
        return this.Get('LoserPerson');
    }

    /**
    * * Field Name: ReversedByPerson
    * * Display Name: Reversed By Name
    * * SQL Data Type: nvarchar(250)
    */
    get ReversedByPerson(): string | null {
        return this.Get('ReversedByPerson');
    }
}


/**
 * Ticket Number Sequences - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: TicketNumberSequence
 * * Base View: vwTicketNumberSequences
 * * @description Per-organization ticket-number counter and prefix (one row per org). Backs the Izzy-side per-org TicketNumber ({PREFIX}-####) that layers on top of the per-AppScope Issue.IssueNumber. TicketPrefix is admin-editable via the entity layer; NextSequenceNumber is maintained ONLY by spAssignNextTicketNumber — never write it directly.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ticket Number Sequences')
export class IzzyTicketNumberSequenceEntity extends BaseEntity<IzzyTicketNumberSequenceEntityType> {
    /**
    * Loads the Ticket Number Sequences record from the database
    * @param ID: string - primary key value to load the Ticket Number Sequences record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyTicketNumberSequenceEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * Validate() method override for Ticket Number Sequences entity. This is an auto-generated method that invokes the generated validators for this entity for the following fields:
    * * NextSequenceNumber: The next sequence number must be a positive integer greater than zero.
    * @public
    * @method
    * @override
    */
    public override Validate(): ValidationResult {
        const result = super.Validate();
        this.ValidateNextSequenceNumberGreaterThanZero(result);
        result.Success = result.Success && (result.Errors.length === 0);

        return result;
    }

    /**
    * The next sequence number must be a positive integer greater than zero.
    * @param result - the ValidationResult object to add any errors or warnings to
    * @public
    * @method
    */
    public ValidateNextSequenceNumberGreaterThanZero(result: ValidationResult) {
    	if (this.NextSequenceNumber !== undefined && this.NextSequenceNumber !== null && this.NextSequenceNumber <= 0) {
    		result.Errors.push(new ValidationErrorInfo(
    			"NextSequenceNumber",
    			"The next sequence number must be greater than zero.",
    			this.NextSequenceNumber,
    			ValidationErrorType.Failure
    		));
    	}
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization this counter belongs to (UNIQUE — one sequence row per org). FK to bizapps-common Organization.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: TicketPrefix
    * * Display Name: Ticket Prefix
    * * SQL Data Type: nvarchar(20)
    * * Default Value: TKT
    * * Description: Per-org ticket prefix (e.g. 'ACME'). Admin-editable; defaults to 'TKT'. Combined with the sequence as {PREFIX}-{padded seq}. Changing it does not renumber existing tickets — the counter keeps climbing.
    */
    get TicketPrefix(): string {
        return this.Get('TicketPrefix');
    }
    set TicketPrefix(value: string) {
        this.Set('TicketPrefix', value);
    }

    /**
    * * Field Name: NextSequenceNumber
    * * Display Name: Next Sequence Number
    * * SQL Data Type: int
    * * Default Value: 1
    * * Description: The next sequence value to assign for this org. Incremented atomically (UPDLOCK/HOLDLOCK) by spAssignNextTicketNumber.
    */
    get NextSequenceNumber(): number {
        return this.Get('NextSequenceNumber');
    }
    set NextSequenceNumber(value: number) {
        this.Set('NextSequenceNumber', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization Name
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }
}


/**
 * Ticket SLA States - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: TicketSLAState
 * * Base View: vwTicketSLAStates
 * * @description The SLA clock for a single ticket (1:1 with the bizapps-issues Issue). Tracks first-response and resolution due dates, paused time accumulated in non-counting statuses, and warning/breach state.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ticket SLA States')
export class IzzyTicketSLAStateEntity extends BaseEntity<IzzyTicketSLAStateEntityType> {
    /**
    * Loads the Ticket SLA States record from the database
    * @param ID: string - primary key value to load the Ticket SLA States record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyTicketSLAStateEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: IssueID
    * * Display Name: Ticket
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
    * * Description: The ticket (bizapps-issues Issue) this SLA clock tracks. One SLA state per Issue.
    */
    get IssueID(): string {
        return this.Get('IssueID');
    }
    set IssueID(value: string) {
        this.Set('IssueID', value);
    }

    /**
    * * Field Name: SLAPolicyID
    * * Display Name: SLA Policy ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: SLA Policies (vwSLAPolicies.ID)
    * * Description: The SLA policy matched to this ticket at creation; NULL if no policy matched.
    */
    get SLAPolicyID(): string | null {
        return this.Get('SLAPolicyID');
    }
    set SLAPolicyID(value: string | null) {
        this.Set('SLAPolicyID', value);
    }

    /**
    * * Field Name: FirstResponseAt
    * * Display Name: First Response At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp of the first agent/AI response to the ticket; NULL until first response. Stamped once.
    */
    get FirstResponseAt(): Date | null {
        return this.Get('FirstResponseAt');
    }
    set FirstResponseAt(value: Date | null) {
        this.Set('FirstResponseAt', value);
    }

    /**
    * * Field Name: FirstResponseDueAt
    * * Display Name: First Response Due At
    * * SQL Data Type: datetimeoffset
    * * Description: Deadline for the first response, computed from the policy target (adjusted for paused time and business hours).
    */
    get FirstResponseDueAt(): Date | null {
        return this.Get('FirstResponseDueAt');
    }
    set FirstResponseDueAt(value: Date | null) {
        this.Set('FirstResponseDueAt', value);
    }

    /**
    * * Field Name: ResolutionDueAt
    * * Display Name: Resolution Due At
    * * SQL Data Type: datetimeoffset
    * * Description: Deadline for resolution, computed from the policy target (adjusted for paused time and business hours).
    */
    get ResolutionDueAt(): Date | null {
        return this.Get('ResolutionDueAt');
    }
    set ResolutionDueAt(value: Date | null) {
        this.Set('ResolutionDueAt', value);
    }

    /**
    * * Field Name: SLAPausedAt
    * * Display Name: SLA Paused At
    * * SQL Data Type: datetimeoffset
    * * Description: When the ticket last entered a non-counting (paused) status; NULL when the clock is running. Used to accumulate SLAPausedMinutes on resume.
    */
    get SLAPausedAt(): Date | null {
        return this.Get('SLAPausedAt');
    }
    set SLAPausedAt(value: Date | null) {
        this.Set('SLAPausedAt', value);
    }

    /**
    * * Field Name: SLAPausedMinutes
    * * Display Name: SLA Paused Minutes
    * * SQL Data Type: int
    * * Default Value: 0
    * * Description: Total minutes the clock has been paused across all non-counting periods; added to due-date computations.
    */
    get SLAPausedMinutes(): number {
        return this.Get('SLAPausedMinutes');
    }
    set SLAPausedMinutes(value: number) {
        this.Set('SLAPausedMinutes', value);
    }

    /**
    * * Field Name: WarningFiredAt
    * * Display Name: Warning Fired At
    * * SQL Data Type: datetimeoffset
    * * Description: When the pre-breach warning fired for this ticket; NULL until fired. Prevents duplicate warnings.
    */
    get WarningFiredAt(): Date | null {
        return this.Get('WarningFiredAt');
    }
    set WarningFiredAt(value: Date | null) {
        this.Set('WarningFiredAt', value);
    }

    /**
    * * Field Name: Breached
    * * Display Name: Breached
    * * SQL Data Type: bit
    * * Default Value: 0
    * * Description: 1 when the resolution SLA has been breached.
    */
    get Breached(): boolean {
        return this.Get('Breached');
    }
    set Breached(value: boolean) {
        this.Set('Breached', value);
    }

    /**
    * * Field Name: BreachedAt
    * * Display Name: Breached At
    * * SQL Data Type: datetimeoffset
    * * Description: Timestamp the breach was recorded; NULL when not breached.
    */
    get BreachedAt(): Date | null {
        return this.Get('BreachedAt');
    }
    set BreachedAt(value: Date | null) {
        this.Set('BreachedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Issue
    * * Display Name: Issue
    * * SQL Data Type: nvarchar(50)
    */
    get Issue(): string | null {
        return this.Get('Issue');
    }

    /**
    * * Field Name: SLAPolicy
    * * Display Name: SLA Policy
    * * SQL Data Type: nvarchar(255)
    */
    get SLAPolicy(): string | null {
        return this.Get('SLAPolicy');
    }
}


/**
 * Ticket Suggestions - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: TicketSuggestion
 * * Base View: vwTicketSuggestions
 * * @description A classifier-suggested (never auto-applied) link between a newly-created ticket and an existing open ticket from the same reporter. Surfaced on the new ticket for staff to confirm (merge) or dismiss. Created only when the reporter has more than one open ticket, where auto-linking would risk merging the wrong conversations.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ticket Suggestions')
export class IzzyTicketSuggestionEntity extends BaseEntity<IzzyTicketSuggestionEntityType> {
    /**
    * Loads the Ticket Suggestions record from the database
    * @param ID: string - primary key value to load the Ticket Suggestions record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyTicketSuggestionEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: NewIssueID
    * * Display Name: New Ticket
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
    * * Description: The ticket (bizapps-issues Issue) the follow-up message actually created; this is the ticket whose detail view shows the suggestion banner.
    */
    get NewIssueID(): string {
        return this.Get('NewIssueID');
    }
    set NewIssueID(value: string) {
        this.Set('NewIssueID', value);
    }

    /**
    * * Field Name: SuggestedIssueID
    * * Display Name: Suggested Ticket
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Issues: Issues (vwIssues.ID)
    * * Description: The existing open ticket (bizapps-issues Issue) the new ticket may be a follow-up to — the merge target if staff accept the suggestion.
    */
    get SuggestedIssueID(): string {
        return this.Get('SuggestedIssueID');
    }
    set SuggestedIssueID(value: string) {
        this.Set('SuggestedIssueID', value);
    }

    /**
    * * Field Name: TriggerMessageID
    * * Display Name: Trigger Message ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Channel Messages (vwChannelMessages.ID)
    * * Description: The inbound ChannelMessage that triggered the suggestion (the follow-up); NULL if not retained.
    */
    get TriggerMessageID(): string | null {
        return this.Get('TriggerMessageID');
    }
    set TriggerMessageID(value: string | null) {
        this.Set('TriggerMessageID', value);
    }

    /**
    * * Field Name: ReporterPersonID
    * * Display Name: Reporter ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The reporter Person both tickets share; provenance for the same-person guard that gated the suggestion.
    */
    get ReporterPersonID(): string | null {
        return this.Get('ReporterPersonID');
    }
    set ReporterPersonID(value: string | null) {
        this.Set('ReporterPersonID', value);
    }

    /**
    * * Field Name: Reason
    * * Display Name: Reason
    * * SQL Data Type: nvarchar(1000)
    * * Description: The classifier's short rationale for suggesting the link (shown to staff).
    */
    get Reason(): string | null {
        return this.Get('Reason');
    }
    set Reason(value: string | null) {
        this.Set('Reason', value);
    }

    /**
    * * Field Name: Confidence
    * * Display Name: Confidence
    * * SQL Data Type: decimal(5, 4)
    * * Description: Classifier confidence in the suggested link, 0 to 1, when the classifier provides one; NULL otherwise.
    */
    get Confidence(): number | null {
        return this.Get('Confidence');
    }
    set Confidence(value: number | null) {
        this.Set('Confidence', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Open
    * * Value List Type: List
    * * Possible Values 
    *   * Accepted
    *   * Dismissed
    *   * Open
    * * Description: Suggestion lifecycle: Open = awaiting staff action; Accepted = staff merged the tickets; Dismissed = staff rejected the link.
    */
    get Status(): 'Accepted' | 'Dismissed' | 'Open' {
        return this.Get('Status');
    }
    set Status(value: 'Accepted' | 'Dismissed' | 'Open') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: ResolvedByPersonID
    * * Display Name: Resolved By ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The staff Person who accepted or dismissed the suggestion; NULL while Open.
    */
    get ResolvedByPersonID(): string | null {
        return this.Get('ResolvedByPersonID');
    }
    set ResolvedByPersonID(value: string | null) {
        this.Set('ResolvedByPersonID', value);
    }

    /**
    * * Field Name: ResolvedAt
    * * Display Name: Resolved At
    * * SQL Data Type: datetimeoffset
    * * Description: When the suggestion was accepted or dismissed; NULL while Open.
    */
    get ResolvedAt(): Date | null {
        return this.Get('ResolvedAt');
    }
    set ResolvedAt(value: Date | null) {
        this.Set('ResolvedAt', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: NewIssue
    * * Display Name: New Issue
    * * SQL Data Type: nvarchar(50)
    */
    get NewIssue(): string | null {
        return this.Get('NewIssue');
    }

    /**
    * * Field Name: SuggestedIssue
    * * Display Name: Suggested Issue
    * * SQL Data Type: nvarchar(50)
    */
    get SuggestedIssue(): string | null {
        return this.Get('SuggestedIssue');
    }

    /**
    * * Field Name: TriggerMessage
    * * Display Name: Trigger Message
    * * SQL Data Type: nvarchar(500)
    */
    get TriggerMessage(): string | null {
        return this.Get('TriggerMessage');
    }

    /**
    * * Field Name: ReporterPerson
    * * Display Name: Reporter
    * * SQL Data Type: nvarchar(250)
    */
    get ReporterPerson(): string | null {
        return this.Get('ReporterPerson');
    }

    /**
    * * Field Name: ResolvedByPerson
    * * Display Name: Resolved By
    * * SQL Data Type: nvarchar(250)
    */
    get ResolvedByPerson(): string | null {
        return this.Get('ResolvedByPerson');
    }
}


/**
 * Ticket Team Members - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: TicketTeamMember
 * * Base View: vwTicketTeamMembers
 * * @description A person's membership in a ticket team, carrying their role, availability, and ticket capacity for routing.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ticket Team Members')
export class IzzyTicketTeamMemberEntity extends BaseEntity<IzzyTicketTeamMemberEntityType> {
    /**
    * Loads the Ticket Team Members record from the database
    * @param ID: string - primary key value to load the Ticket Team Members record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyTicketTeamMemberEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: TeamID
    * * Display Name: Team
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: Ticket Teams (vwTicketTeams.ID)
    * * Description: The team this membership belongs to.
    */
    get TeamID(): string {
        return this.Get('TeamID');
    }
    set TeamID(value: string) {
        this.Set('TeamID', value);
    }

    /**
    * * Field Name: PersonID
    * * Display Name: Person
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: People (vwPeople.ID)
    * * Description: The person (support agent) who is a member of the team.
    */
    get PersonID(): string {
        return this.Get('PersonID');
    }
    set PersonID(value: string) {
        this.Set('PersonID', value);
    }

    /**
    * * Field Name: Role
    * * Display Name: Role
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Member
    * * Value List Type: List
    * * Possible Values 
    *   * Lead
    *   * Member
    * * Description: Member role within the team: Lead or Member.
    */
    get Role(): 'Lead' | 'Member' {
        return this.Get('Role');
    }
    set Role(value: 'Lead' | 'Member') {
        this.Set('Role', value);
    }

    /**
    * * Field Name: IsAvailable
    * * Display Name: Is Available
    * * SQL Data Type: bit
    * * Default Value: 1
    * * Description: Whether the member is currently available to receive routed tickets (e.g. toggled off when out of office).
    */
    get IsAvailable(): boolean {
        return this.Get('IsAvailable');
    }
    set IsAvailable(value: boolean) {
        this.Set('IsAvailable', value);
    }

    /**
    * * Field Name: MaxConcurrentTickets
    * * Display Name: Max Concurrent Tickets
    * * SQL Data Type: int
    * * Description: Maximum number of concurrently-open tickets the member should hold; NULL means no cap. Used by least-load routing to skip over-capacity agents.
    */
    get MaxConcurrentTickets(): number | null {
        return this.Get('MaxConcurrentTickets');
    }
    set MaxConcurrentTickets(value: number | null) {
        this.Set('MaxConcurrentTickets', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
    * * Description: Lifecycle status: Active = counted for routing, Inactive = retained but not routed to.
    */
    get Status(): 'Active' | 'Inactive' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Inactive') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Team
    * * Display Name: Team Name
    * * SQL Data Type: nvarchar(255)
    */
    get Team(): string {
        return this.Get('Team');
    }

    /**
    * * Field Name: Person
    * * Display Name: Person Name
    * * SQL Data Type: nvarchar(250)
    */
    get Person(): string {
        return this.Get('Person');
    }
}


/**
 * Ticket Teams - strongly typed entity sub-class
 * * Schema: Izzy
 * * Base Table: TicketTeam
 * * Base View: vwTicketTeams
 * * @description A named group of support agents within an organization, used for ticket assignment and routing.
 * * Primary Key: ID
 * @extends {BaseEntity}
 * @class
 * @public
 */
@RegisterClass(BaseEntity, 'Ticket Teams')
export class IzzyTicketTeamEntity extends BaseEntity<IzzyTicketTeamEntityType> {
    /**
    * Loads the Ticket Teams record from the database
    * @param ID: string - primary key value to load the Ticket Teams record.
    * @param EntityRelationshipsToLoad - (optional) the relationships to load
    * @returns {Promise<boolean>} - true if successful, false otherwise
    * @public
    * @async
    * @memberof IzzyTicketTeamEntity
    * @method
    * @override
    */
    public async Load(ID: string, EntityRelationshipsToLoad?: string[]) : Promise<boolean> {
        const compositeKey: CompositeKey = new CompositeKey();
        compositeKey.KeyValuePairs.push({ FieldName: 'ID', Value: ID });
        return await super.InnerLoad(compositeKey, EntityRelationshipsToLoad);
    }

    /**
    * * Field Name: ID
    * * Display Name: ID
    * * SQL Data Type: uniqueidentifier
    * * Default Value: newsequentialid()
    */
    get ID(): string {
        return this.Get('ID');
    }
    set ID(value: string) {
        this.Set('ID', value);
    }

    /**
    * * Field Name: OrganizationID
    * * Display Name: Organization ID
    * * SQL Data Type: uniqueidentifier
    * * Related Entity/Foreign Key: MJ_BizApps_Common: Organizations (vwOrganizations.ID)
    * * Description: The organization that owns this team.
    */
    get OrganizationID(): string {
        return this.Get('OrganizationID');
    }
    set OrganizationID(value: string) {
        this.Set('OrganizationID', value);
    }

    /**
    * * Field Name: Name
    * * Display Name: Name
    * * SQL Data Type: nvarchar(255)
    * * Description: Display name of the team (e.g. "Billing", "Tier 1").
    */
    get Name(): string {
        return this.Get('Name');
    }
    set Name(value: string) {
        this.Set('Name', value);
    }

    /**
    * * Field Name: Description
    * * Display Name: Description
    * * SQL Data Type: nvarchar(MAX)
    * * Description: Optional longer description of the team's scope or responsibilities.
    */
    get Description(): string | null {
        return this.Get('Description');
    }
    set Description(value: string | null) {
        this.Set('Description', value);
    }

    /**
    * * Field Name: Status
    * * Display Name: Status
    * * SQL Data Type: nvarchar(20)
    * * Default Value: Active
    * * Value List Type: List
    * * Possible Values 
    *   * Active
    *   * Inactive
    * * Description: Lifecycle status: Active = available for routing, Inactive = retained but not routed to.
    */
    get Status(): 'Active' | 'Inactive' {
        return this.Get('Status');
    }
    set Status(value: 'Active' | 'Inactive') {
        this.Set('Status', value);
    }

    /**
    * * Field Name: __mj_CreatedAt
    * * Display Name: Created At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_CreatedAt(): Date {
        return this.Get('__mj_CreatedAt');
    }

    /**
    * * Field Name: __mj_UpdatedAt
    * * Display Name: Updated At
    * * SQL Data Type: datetimeoffset
    * * Default Value: getutcdate()
    */
    get __mj_UpdatedAt(): Date {
        return this.Get('__mj_UpdatedAt');
    }

    /**
    * * Field Name: Organization
    * * Display Name: Organization
    * * SQL Data Type: nvarchar(255)
    */
    get Organization(): string {
        return this.Get('Organization');
    }
}
